// Statement-config admin — server client.
//
// Each method calls the matching VB DataService partial (StatementMgmt.vb):
//   WebStatementConfigList / WebStatementConfigGet / WebStatementResolveConfig /
//   WebStatementConfigSave / WebStatementConfigDeactivate / WebStatementSampleCalc.
//
// All data comes from the database — no client-side fixtures. On a server
// error we surface the empty result so the page's empty-override / unpicked
// branches kick in, and the failure is logged on the way up.
import { servicePost } from "./client.ts";
import type { ReturnObject } from "../types/returnObject.ts";
import type { ExportInfo } from "../types/grid.ts";

interface ServerVersionDto {
  ID: number;
  Scope: ScopeLayer;
  ScopeID: number;
  ConfigData: string;
  /** Public-only envelope JSON ({keyId, info} per sensitive key). Server
   *  strips ciphertext before this leaves the DataService. */
  SensitiveData: string | null;
  ValidFrom: string;
  ValidTo: string | null;
  Created: string;
  CreatedBy: string;
  IsActive: boolean;
}

function mapServerVersion(v: ServerVersionDto, scopeLabel: string): StatementConfigVersion {
  let data: StatementConfigData = {};
  try { data = JSON.parse(v.ConfigData || "{}"); } catch { /* keep empty */ }
  let payoutInfo: PayoutInfoPublic | null = null;
  if (v.SensitiveData) {
    try {
      const parsed = JSON.parse(v.SensitiveData) as { payoutInfo?: PayoutInfoPublic };
      if (parsed.payoutInfo && parsed.payoutInfo.info && typeof parsed.payoutInfo.info === "object") {
        payoutInfo = parsed.payoutInfo;
      }
    } catch { /* keep null */ }
  }
  let odooSecret: OdooSecretPublic | null = null;
  if (v.SensitiveData) {
    try {
      const parsed = JSON.parse(v.SensitiveData) as { odooSecret?: OdooSecretPublic };
      if (parsed.odooSecret && parsed.odooSecret.info && typeof parsed.odooSecret.info === "object") {
        odooSecret = parsed.odooSecret;
      }
    } catch { /* keep null */ }
  }
  return {
    configID: v.ID,
    scope: v.Scope,
    scopeID: v.ScopeID,
    scopeLabel,
    validFrom: (v.ValidFrom ?? "").slice(0, 10),
    validTo: v.ValidTo ? v.ValidTo.slice(0, 10) : null,
    isActive: !!v.IsActive,
    createdBy: v.CreatedBy,
    createdAt: v.Created,
    data,
    payoutInfo,
    odooSecret,
    changedKeys: Object.keys(data),
    seed: /(_SP|^system$)/i.test(v.CreatedBy ?? ""),
  };
}

export type ScopeLayer = "DATABASE" | "OPERATOR" | "KIOSK" | "RESELLER" | "RESELLER_INBOUND";

// ── Reseller CRUD (multi-level statements, Phase 2) ────────────────────────

export interface Reseller { id: number; name: string; isActive: boolean; }

export async function listResellers(includeInactive = false): Promise<Reseller[]> {
  const r = await servicePost<Array<{ ID: number; Name: string; IsActive: boolean }>>(
    "WebStatementResellerList", { includeInactive });
  return (r.oData ?? []).map(x => ({ id: x.ID, name: x.Name, isActive: x.IsActive }));
}
export async function saveReseller(id: number, name: string): Promise<number> {
  const r = await servicePost<number>("WebStatementResellerSave", { id, name });
  return r.oData ?? 0;
}
/** Returns the full ReturnObject so the caller can surface bError/sMessage —
 *  server sets a friendly message when operators are still assigned. */
export async function deactivateReseller(id: number): Promise<ReturnObject<boolean>> {
  return servicePost<boolean>("WebStatementResellerDeactivate", { id });
}

/** Eligible payment-method groups (platform truth from
 *  dbo.S_Statement_PaymentGroup_FN, e.g. ["C&P","IPA"]) — one bookkeeping
 *  account row per group in the client's bookkeeping panel. Empty array
 *  (rather than a thrown error) both before the TVF is deployed and on any
 *  other server error, matching the VB endpoint's return-empty-on-error. */
export async function listBookKeepingGroups(): Promise<string[]> {
  const r = await servicePost<string[]>("WebStatementBookKeepingGroups", {});
  return r.oData ?? [];
}

/** One transaction behind one posted accrual line. Shape matches
 *  StatementVerificationDetailDto and, through it, S_Statement_Verification_Detail_SP. */
export interface VerificationDetailRow {
  statementDate: string;
  operatorID: number;
  account: string | null;
  /** What happened: sale, refund, lost, captureShortfall. */
  occurrence: string | null;
  /** debit or credit, as the operator's posting rule set it. */
  side: string | null;
  /** Which amount the line took: net, vat or gross. */
  amountKind: string | null;
  currency: string | null;
  /** The rate this line is restricted to; null when it takes the whole amount. */
  entryTaxRate: number | null;
  kioskID: number;
  transactionID: number;
  /** The receipt the amounts were read from — the immutable record. */
  receiptID: number | null;
  lineTaxRate: number | null;
  price: number;
  tax: number;
  total: number;
  /** The original sale, which is also the accrual date. */
  saleDate: string | null;
}

/** The transactions behind one day's posted accrual entries — the drill-down from a ledger entry
 *  back to the sales it is made of.
 *
 *  Returns [] rather than throwing when the day has no accrual bookkeeping row: a day nobody
 *  posted is an ordinary state, not a fault, and the panel says so. */
export async function listVerificationDetail(
  statementDate: string, operatorID: number,
  account?: string, occurrence?: string,
): Promise<VerificationDetailRow[]> {
  const r = await servicePost<VerificationDetailRow[]>("WebStatementVerificationDetail", {
    statementDate, operatorID,
    account: account ?? "", occurrence: occurrence ?? "",
  });
  return r.oData ?? [];
}

/** Server JSON shape (matches S_Statement_SeedConfig_SP / the generic fee model). */
export interface LadderStep { from: number; to: number; percentage: number; }

/* ── Generic fee model ──────────────────────────────────────────────────────
 * Every fee is one entry in `fees[]` — there is no logical difference between
 * a device fee, an EMV fee, or an ad-hoc extra fee; only the calcMethod (and
 * its params) differ. Mirrors the SQL engine in S_Statement_DailyClose_SP /
 * S_Statement_Aggregate_SP:
 *   Daily stage (prorated /daysInMonth):
 *     perKiosk        — flat monthly amount per kiosk. params.condition gates
 *                       (emvPresent | rentalActive); params.kpiAdjust applies
 *                       the kpiAdjustments index as
 *                       index[closing year] / index[the year this fee's OWN
 *                       config row became valid]. A price agreed in 2026 is
 *                       therefore not uplifted in 2026 and still tracks the
 *                       index in 2027 — which is why "do not uplift this yet"
 *                       is never kpiAdjust:false, that freezes it forever.
 *     perDeviceCount  — flat monthly amount × device count of params.deviceType.
 *     Both flat methods take params.chargeBasis (CHARGE_BASES below): which
 *     kiosk-days carry the monthly price. Absent = activeMonth, the rule of the
 *     technical-fee report the invoices were raised from before this system.
 *     perTransaction  — per settled receipt: MAX(fee + params.revenuePercent% ×
 *                       amount, params.minPerTxn).
 *     revenuePercent  — fee% of the day's charged revenue, floored at
 *                       params.minPerKioskMonthly (prorated).
 *   Both percentage methods take params.revenueBase: "net" (default, ex tax —
 *   net sales) or "gross" (incl tax — what the cardholder paid). They differ by
 *   the whole VAT: at 25% tax a 7% fee on gross is 8.75% of net. revenueLadder
 *   has always used net, so "net" is also what keeps the methods agreeing.
 *   Aggregate stage:
 *     revenueLadder   — tiered % on period revenue (params.ladderSteps).
 *                       params.scope "operator" (default) runs the steps on this
 *                       operator's own revenue; "database" pools it with every
 *                       operator sharing the ladder in THIS database (see
 *                       params.ladderGroup) and allocates by revenue weight;
 *                       "group" defers to the group's GLOBAL database
 *                       (params.globalDatabase) — the ladder itself is
 *                       configured once there, this entry is just the marker.
 *     perOperator     — flat once per operator per period.
 * Currency: fee values are supplied in the INGOING currency and billed in the
 * OUTGOING currency (per-fee override → config default → root currency). */
export type CalcMethod =
  | "perKiosk" | "perDeviceCount" | "perTransaction"
  | "revenuePercent" | "revenueLadder" | "perOperator";

export type DeviceType = "iqBox" | "sgBox" | "smartBox";

/** Detail sheets the invoice processor generates and attaches to the Odoo invoice.
 *  "kioskTechFee" = per-kiosk Technical Fee xlsx, "ipgDetail" = per-merchant IPG xlsx. */
/** The four shapes, with the legacy names folded in. Absent is `Both`, the documented default.
 *  ONE place decides what a stored code means, so a new shape cannot be silently excluded from a
 *  call site that re-derived the answer with `=== "Payout"` - which is exactly what happened to
 *  StrictPayout when it was added. See billingShape / chargesFees / isPayoutMode below. */
/** A config boolean, tolerant of the resolver's typing.
 *
 *  S_Statement_ResolveConfig_FN merges top-level scalars with JSON_VALUE, which returns a
 *  STRING — so a resolved config carries "waterfallNetProcessorFee":"false", and plain
 *  `if (value)` reads that as ON. The string "false" is the dangerous case: it is truthy in
 *  JavaScript, so a toggle rendered from it shows the opposite of what is stored.
 *
 *  Nested objects (odoo, bookKeeping) keep their real JSON types — the former is merged whole
 *  with JSON_QUERY, the latter casts its booleans to BIT — so this is only needed for the
 *  top-level scalars. */
export function configBool(v: unknown): boolean | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const t = `${v}`.trim().toLowerCase();
  if (t === "false" || t === "0") return false;
  if (t === "true" || t === "1") return true;
  return undefined;
}

/** What an edit to the daily-close block should be MERGED ONTO, so an override scope stores only
 *  what it actually sets.
 *
 *  `bookKeeping` is the one key S_Statement_ResolveConfig_FN merges per member (2026-08-27), so an
 *  operator row holding `{"enabled":true}` keeps following the Database journal, clearing account
 *  and VAT chart — and picks up a member added at Database later. Every other key replaces whole,
 *  which is why this rule is not general.
 *
 *  The case it fixes: a scope with no config row yet starts its draft from the RESOLVED config, so
 *  the first edit would otherwise copy the entire inherited block onto the new row and pin it there
 *  for good. Starting from nothing means the row stores the toggle and nothing else.
 *
 *  Once the row does own the key (`ownsKey`), its own stored block is the base — that is what the
 *  user is looking at and editing. At DATABASE scope there is nothing above to inherit from, so the
 *  draft IS the whole snapshot. */
export function ledgerEditBase(
  scope: ScopeLayer,
  ownsKey: boolean,
  own: BookKeepingConfig | undefined,
): BookKeepingConfig {
  if (scope === "DATABASE" || ownsKey) return { ...(own ?? {}) };
  return {};
}

/** The value to write for one of the daily-close MAPS — `accounts`, `vatAccounts`,
 *  `accountsByCurrency`.
 *
 *  These three do NOT deep-merge: the resolver takes whichever scope sets the map and uses it
 *  whole, because a chart of accounts assembled from two layers would post real money to a
 *  combination nobody configured. So a sparse write is exactly wrong here — storing one method
 *  group's account on an operator row would silently drop every other group the Database mapped.
 *  Editing a map therefore seeds from the row's own copy if it has one, and otherwise from the
 *  inherited one, so what gets stored is always the complete map. */
export function ledgerMapBase<T extends Record<string, unknown>>(
  own: T | undefined,
  inherited: T | undefined,
): T {
  const source = own && Object.keys(own).length > 0 ? own : inherited;
  return { ...((source ?? {}) as T) };
}

export type BillingShape = "Fees" | "PayoutOnly" | "Both" | "BothSplit";

export function billingShape(mode: StatementConfigData["billingMode"]): BillingShape {
  switch (mode) {
    case "Fees": case "FeeInvoice":  return "Fees";
    case "PayoutOnly":               return "PayoutOnly";
    case "BothSplit":                return "BothSplit";
    case "Both": case "Payout":      return "Both";
    default:                         return "Both";
  }
}

/** Does this shape charge the period's fees to the operator? */
export function chargesFees(mode: StatementConfigData["billingMode"]): boolean {
  return billingShape(mode) !== "PayoutOnly";
}

/** Does this shape pay out what the daily closes collected - i.e. does it need a payout bank on
 *  file, and is it restricted to the DIRECT tier? */
export function isPayoutMode(mode: StatementConfigData["billingMode"]): boolean {
  return billingShape(mode) !== "Fees";
}

/** The code a set of answers stores. Only four of the eight combinations are legal, and the two
 *  with neither side are not a shape at all - the caller keeps at least one switch on. */
export function billingModeFor(fees: boolean, payout: boolean, combine: boolean): BillingShape {
  if (fees && payout) return combine ? "Both" : "BothSplit";
  if (payout) return "PayoutOnly";
  return "Fees";
}

export const ATTACHMENT_TYPES = [
  "kioskTechFee", "ipgDetail", "feeCalculation", "kioskInventory",
  "collectionDetail", "refundDetail", "vatSummary", "unpaidDetail",
] as const;
export type AttachmentType = (typeof ATTACHMENT_TYPES)[number];

export const ATTACHMENT_LABEL: Record<AttachmentType, string> = {
  kioskTechFee:     "Kiosk Technical Fee",
  ipgDetail:        "IPG detail (per merchant)",
  feeCalculation:   "Fee calculation (ladder steps, KPI factor)",
  kioskInventory:   "Kiosk inventory (days closed per kiosk)",
  collectionDetail: "Collection detail (per day and method group)",
  refundDetail:     "Refund detail",
  vatSummary:       "VAT summary (per rate)",
  unpaidDetail:     "Unpaid transactions",
};

/** Which DOCUMENT each sheet lands on when a period drafts two of them (billingMode BothSplit).
 *  Both of today's sheets justify FEES — the per-kiosk technical fee, and the per-merchant detail
 *  the fee percentages are computed from — so they ride with the fee invoice and the payout bill
 *  goes out clean. A netting document (Both) carries the collection AND the fees, so it takes
 *  everything. Mirrors InvoiceAttachmentBuilder.SideOf, which is the authority; this copy exists
 *  only to label the checkboxes. */
export const ATTACHMENT_SIDE: Record<AttachmentType, "fee" | "payout" | "both"> = {
  // What the operator is CHARGED
  kioskTechFee:     "fee",
  ipgDetail:        "fee",
  feeCalculation:   "fee",
  kioskInventory:   "fee",
  // What the operator is PAID, and why it is not more
  collectionDetail: "payout",
  refundDetail:     "payout",
  // The per-rate tax split appears on no line of either document, so it belongs on both
  vatSummary:       "both",
  // What was never collected explains a smaller payout, and on a fee invoice it is the
  // operator's own outstanding receivable. Either document raises the question.
  unpaidDetail:     "both",
};
/** Sheets that explain a bookkeeping CLOSE and nothing else: downloadable from a close, never an
 *  invoice attachment. Kept OUT of `ATTACHMENT_TYPES` on purpose, because that list is the
 *  `invoiceAttachments` vocabulary the config editor offers and `validateConfig` accepts; an
 *  accrual posting belongs to a day's ledger entry, not to an Odoo invoice. Mirrors
 *  `StatementSheets.CloseOnly` on the server. */
export const CLOSE_SHEET_TYPES = ["accrualDetail"] as const;
export type CloseSheetType = (typeof CLOSE_SHEET_TYPES)[number];

/** Every sheet the detail-sheet download accepts. */
export type StatementSheetType = AttachmentType | CloseSheetType;

export const CLOSE_SHEET_LABEL: Record<CloseSheetType, string> = {
  accrualDetail: "Accrual detail (posted lines and the sales behind them)",
};

export function isCloseSheet(sheet: StatementSheetType): sheet is CloseSheetType {
  return (CLOSE_SHEET_TYPES as readonly string[]).includes(sheet);
}

export type FeeCondition = "emvPresent" | "rentalActive";
export const INVOICE_TO = ["database", "operator"] as const;
export type InvoiceTo = typeof INVOICE_TO[number];
export const BILLING_STATUSES = ["verified", "unverified"] as const;
export type BillingStatus = typeof BILLING_STATUSES[number];
export interface InvoiceReferences { yourReference?: string; yourOrderNo?: string; ourReference?: string; }

/** Which kiosk-days a flat monthly fee (perKiosk / perDeviceCount) is charged for. Mirrors
 *  #T_ChargeKiosk in S_Statement_DailyClose_SP. "Active" = the kiosk reported
 *  (Kiosk_Devices_History) or took money; "LIVE" = Kiosk_InHouse_History status. A month basis
 *  charges the whole month: its first qualifying day also carries the earlier days. */
export const CHARGE_BASES = ["activeMonth", "liveMonth", "salesMonth", "reportedDays", "activeDays", "liveDays"] as const;
export type ChargeBasis = typeof CHARGE_BASES[number];
export const DEFAULT_CHARGE_BASIS: ChargeBasis = "activeMonth";
export const CHARGE_BASIS_LABEL: Record<ChargeBasis, string> = {
  activeMonth:  "Full month if active once (and LIVE)",
  liveMonth:    "Full month if LIVE, active or not",
  salesMonth:   "Full month per kiosk with a sale in it",
  reportedDays: "Each day from first report (while LIVE)",
  activeDays:   "Only days active (while LIVE)",
  liveDays:     "Every day LIVE, active or not",
};

/** params.layer values (S_Statement_DailyClose_SP / Aggregate carry them onto the fee entries). */
export const FEE_LAYERS = ["platform", "owner"] as const;
export type FeeLayer = typeof FEE_LAYERS[number];

export interface FeeParams {
  /** perDeviceCount: which device count the fee multiplies. */
  deviceType?: DeviceType;
  /** perKiosk / perDeviceCount / perOperator (per = database too): apply the yearly kpiAdjustments multiplier. */
  kpiAdjust?: boolean;
  /** With kpiAdjust: the year the amount was agreed, the KPI base. Omitted = the year this config
   *  row became valid, which moves when the row is re-saved; naming it pins the base. */
  kpiBaseYear?: number;
  /** perKiosk: only charge when the condition holds. */
  condition?: FeeCondition;
  /** perKiosk / perDeviceCount: which kiosk-days carry the fee. Omit for activeMonth - or, on an
   *  emvPresent fee, for the card-terminal account rule: the whole month once the kiosk had a
   *  card-present sale. */
  chargeBasis?: ChargeBasis;
  /** perTransaction: charge per settled CHARGE on these payment processors only (SQL LIKE
   *  patterns over the receipt processor, e.g. "PAYTER%", "CEXML IPA%"). The IPG fee: its rate
   *  depends on how the customer paid. Omit to charge per receipt over every charge. */
  processors?: string[];
  /** perOperator: "database" charges the fee once per database and period (a fixed item of the
   *  owner, e.g. Remote Access Service) instead of once per operator. */
  per?: "operator" | "database";
  /** perOperator: units nothing reports, billed the fee each - per database (an Instant ID unit, the Payter
   *  terminals of the owner) or per operator (the Payter terminals of a club). Absent = 1; 0 = none now -
   *  the fee stays in the config and bills nothing. */
  quantity?: number;
  /** Which invoicing layer bills the fee: "platform" (Instant Systems, the default) or "owner"
   *  (the database owner, in an ownerInvoicing database). Required on every fee there. */
  layer?: FeeLayer;
  /** perTransaction: revenue % component added on top of the fixed `fee`. */
  revenuePercent?: number;
  /** perTransaction: per-receipt floor. */
  minPerTxn?: number;
  /** revenuePercent: monthly per-kiosk floor. */
  minPerKioskMonthly?: number;
  /** revenuePercent and perTransaction: what the percentage is OF.
   *  "net" (default) = ex tax, i.e. net sales. "gross" = incl tax, what the
   *  cardholder paid — for fees that really are charged on the card amount,
   *  which acquiring costs are. Omit to mean net. */
  revenueBase?: "net" | "gross";
  /** revenueLadder (scope operator): the tier steps. */
  ladderSteps?: LadderStep[];
  /** revenueLadder: "operator" (default, computed locally) | "group" (computed
   *  on the group's global database from ITS config). */
  scope?: "operator" | "database" | "group";
  /** revenueLadder scope=database: the pool this fee joins. Operators naming the same pool run
   *  the ladder ONCE on their combined revenue and each take a revenue-weighted slice, so a
   *  tiered ladder rewards volume without a group global database. Optional — left unset, a fee
   *  pools with every other database-scoped ladder whose steps are identical, and a label-less
   *  fee also joins a labelled pool whose steps match. An operator on a different ladder (or on
   *  operator scope) contributes nothing to the pool and is billed on its own revenue. */
  ladderGroup?: string;
  /** revenueLadder scope=group: the global database that owns the ladder. */
  globalDatabase?: string;
  /** revenueLadder on a GLOBAL database: which kiosk sub-group this ladder covers. */
  kioskGroup?: string;
  /** Currency the supplied fee values (and revenue evaluation) are in.
   *  Falls back to defaultIngoingCurrency → currency. */
  ingoingCurrency?: string;
  /** Currency the fee is billed in. Falls back to defaultOutgoingCurrency → currency. */
  outgoingCurrency?: string;
  /** Detail sheet the invoice processor generates and attaches to the Odoo
   *  invoice when this fee bills: "kioskTechFee" = per-kiosk Technical Fee xlsx
   *  (needs the group's global database), "ipgDetail" = per-merchant IPG xlsx. */
  attachment?: AttachmentType;
}

export interface FeeEntry {
  name: string;
  calcMethod: CalcMethod;
  /** Main amount — meaning depends on calcMethod (flat amount, % for
   *  revenuePercent, fixed per-txn part for perTransaction; null for ladders). */
  fee: number | null;
  /** External article mapping — open object so a target-system change only
   *  touches the GUI.
   *
   *  `odoo` is our article *reference* (the invoice Article No, e.g. "10052") and
   *  is NOT an Odoo id. Nothing reads it any more (2026-08-27): the processor used
   *  to look it up via product.product.default_code, and that lookup is gone.
   *
   *  `odooId` is the Odoo product.product **database id**, taken from Odoo and
   *  entered here — the processor sends it as the invoice line's product_id
   *  verbatim. Null/absent is normal and means the fee is billed BY NAME against
   *  the Odoo profile's fee account (an article number is not required to invoice
   *  a fee); a value that is present but not a positive id FAILS the invoice
   *  rather than quietly billing by name, because a mapped fee's revenue belongs
   *  on its product. */
  articleNumber: { odoo: string | null; odooId?: number | null };
  params?: FeeParams;
}

/** A payment plan (spec 26): a fixed schedule billed once a month by S_Statement_Aggregate_SP - a
 *  DATABASE plan on the database row, an OPERATOR plan on that operator's row - computed from the plan
 *  alone (S_Statement_PlanInstallment_FN, mirrored by planSchedule below). Each scope bills its own
 *  plans: nothing is inherited. An `installment` plan is either `principal` over `months` (financed =
 *  principal minus downPayment, amortised to `residual`, interest optional) or a fixed `monthly` over
 *  `months` (no interest). A `rental` is a `monthly` amount, for `months` or open-ended. */
export interface PaymentPlan {
  /** Stable identity: the schedule and every invoice line key on it, never on the config row. */
  id: string;
  name: string;
  kind?: PlanKind;
  articleNumber?: { odoo: string | null; odooId?: number | null };
  layer?: FeeLayer;
  /** The contract currency, billed as is (never converted); unset = the database outgoing currency. */
  currency?: string;
  /** First month billed, yyyy-mm. */
  start: string;
  /** Required for an instalment plan; a rental without it runs until endsAfter, or for good. */
  months?: number;
  principal?: number;
  monthly?: number;
  interest?: { ratePct: number; method?: PlanInterestMethod };
  downPayment?: number;
  setupFee?: number;
  residual?: number;
  /** Last month billed, yyyy-mm: ends a plan early (a payoff or a restructure starts a new plan). */
  endsAfter?: string;
  /** A rental only (spec 26.9): the rent follows the kpiAdjustments index of this config - x index[year of
   *  the month] / index[kpiBaseYear], the lookup rule of the fees. The set-up fee is not indexed. */
  kpiAdjust?: boolean;
  /** The year the rent was agreed; unset = the year of the first month. */
  kpiBaseYear?: number;
}
/** The longest plan, in months (50 years) - S_Statement_PlanInstallment_FN bills none longer. */
export const PLAN_MAX_MONTHS = 600;
export const PLAN_KINDS = ["installment", "rental"] as const;
export type PlanKind = typeof PLAN_KINDS[number];
/** How many months the preview shows of an open-ended rental. */
export const PLAN_OPEN_PREVIEW_MONTHS = 24;
export const PLAN_INTEREST_METHODS = ["annuity", "straight"] as const;
export type PlanInterestMethod = typeof PLAN_INTEREST_METHODS[number];

/** One instalment, as S_Statement_PlanInstallment_FN computes it. */
export interface PlanInstallment {
  month: string; installment: number;
  /** null: an open-ended rental. */
  of: number | null;
  principalPart: number; interestPart: number; downPayment: number; setupFee: number; residual: number;
  /** null: an open-ended rental. */
  remainingAfter: number | null; amount: number;
  /** An indexed rental: the factor the rent was multiplied by this month. */
  kpiFactor?: number;
}

/** A kpiAdjustments index value for a year, by the rule of S_Statement_ResolveConfig_FN and
 *  S_Statement_PlanLines_FN: the greatest indexed year at or before it, else the earliest; none = undefined. */
export function kpiIndexAt(map: Record<string, number> | undefined, year: number): number | undefined {
  const ys = Object.entries(map ?? {})
    .map(([k, v]) => [/^\d+$/.test(k) ? Number(k) : NaN, Number(v)] as const)
    .filter(([y, v]) => Number.isInteger(y) && Number.isFinite(v))
    .sort((a, b) => a[0] - b[0]);
  if (ys.length === 0) return undefined;
  const at = ys.filter(([y]) => y <= year).pop() ?? ys[0]!;
  return roundTo(at[1], 5);
}

/** The factor an indexed rental's rent is multiplied by in a month: index[year] / index[base year]
 *  (DECIMAL(18,10) as the SQL), 1 when the plan is not indexed or the config has no index. */
export function planKpiFactor(p: PaymentPlan, month: string, kpi: Record<string, number> | undefined): number {
  if (!p.kpiAdjust || p.kind !== "rental") return 1;
  const base = Number.isInteger(p.kpiBaseYear) && p.kpiBaseYear! >= 1900 && p.kpiBaseYear! <= 2999 ? p.kpiBaseYear! : Number(p.start.slice(0, 4));
  const now = kpiIndexAt(kpi, Number(month.slice(0, 4))), b = kpiIndexAt(kpi, base);
  return now == null || !b ? 1 : roundTo(now / b, 10);
}

// SQL ROUND(DECIMAL, 2) / CAST(FLOAT AS DECIMAL(p,s)): half away from zero, positives only here.
const roundTo = (x: number, dp: number) => { const f = 10 ** dp; return Math.round(x * f + Number.EPSILON * f) / f; };
const cents = (x: number) => roundTo(x, 2);

function addMonths(ym: string, k: number): string {
  const [y, m] = ym.split("-").map(Number) as [number, number];
  const t = y * 12 + (m - 1) + k;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
}

/** The whole schedule of a plan - the preview of what S_Statement_PlanInstallment_FN bills month by month.
 *  Same rule: an annuity bills the payment rounded to cents every regular month, a straight plan repays the
 *  same rounded amount plus interest on the balance before it; the last instalment settles what is left.
 *  Balances are the SQL ones (4-6 decimals, then cents). Empty for a plan the SQL would not bill. */
export function planSchedule(p: PaymentPlan, kpi?: Record<string, number>): PlanInstallment[] {
  const ym = /^\d{4}-(0[1-9]|1[0-2])$/;
  if (!ym.test(p.start ?? "")) return [];
  if ((p.kind ?? "installment") === "rental") return rentalSchedule(p, kpi);
  if (p.kind != null && p.kind !== "installment") return [];
  const n = p.months!, dn = p.downPayment ?? 0, su = p.setupFee ?? 0, re = p.residual ?? 0;
  const r = (p.interest?.ratePct ?? 0) / 1200;
  const ann = (p.interest?.method ?? "annuity") === "annuity" && r > 0;
  if (!Number.isInteger(n) || n < 1 || n > PLAN_MAX_MONTHS) return [];
  const byPrincipal = p.principal != null && p.monthly == null && p.principal - dn > re;
  const byMonthly = p.monthly != null && p.principal == null && r === 0;
  if (!byPrincipal && !byMonthly) return [];
  const F = (p.principal ?? 0) - dn;
  const pmt = ann ? cents(roundTo((F - re * (1 + r) ** -n) * r / (1 - (1 + r) ** -n), 6)) : cents(roundTo((F - re) / n, 6));
  const bal = (j: number) => j >= n ? re
    : ann ? cents(roundTo(F * (1 + r) ** j - pmt * ((1 + r) ** j - 1) / r, 6)) : cents(F - j * pmt);
  const out: PlanInstallment[] = [];
  for (let k = 1; k <= n; k++) {
    const month = addMonths(p.start, k - 1);
    if (p.endsAfter && month > p.endsAfter) break;
    const first = k === 1, last = k === n;
    let principalPart: number, interestPart: number, remainingAfter: number;
    if (byMonthly) {
      principalPart = cents(p.monthly!); interestPart = 0; remainingAfter = last ? 0 : cents(p.monthly! * (n - k) + re);
    } else {
      const prev = bal(k - 1), cur = bal(k);
      principalPart = cents(prev - cur);
      interestPart = ann && !last ? cents(pmt - principalPart) : cents(roundTo(prev * r, 6));
      remainingAfter = last ? 0 : cur;
    }
    const amount = cents(principalPart + interestPart + (first ? dn + su : 0) + (last ? re : 0));
    out.push({ month, installment: k, of: n, principalPart, interestPart, downPayment: first ? dn : 0, setupFee: first ? su : 0,
      residual: last ? re : 0, remainingAfter, amount });
  }
  return out;
}

/** The old rental block as a rental plan (spec 26.8, the Rental section's convert): its end month, and
 *  its start month or this month, whichever is earlier - the block never read its start date, so its gate
 *  is on now even before a future start; monthly 0 - the gate alone, as the block was: its feeOverride was never billed, so taking it as
 *  the rent would start a charge the kiosk never had (the fee gated by rentalActive keeps billing the rent).
 *  The end moves to the end of its month: a plan bills whole months. */
export function rentalFromBlock(block: RentalConfig, id: string, thisMonth: string): PaymentPlan {
  const ym = (d: string | null | undefined) => (d && /^\d{4}-\d{2}/.test(d) ? d.slice(0, 7) : undefined);
  const from = ym(block.startDate);
  const plan: PaymentPlan = { id, name: "Rental", kind: "rental", start: from && from < thisMonth ? from : thisMonth,
    monthly: 0, articleNumber: { odoo: null } };
  const end = ym(block.endDate);
  if (end) plan.endsAfter = end;
  return plan;
}

/** The rental block a KIOSK rental plan makes (spec 26.8), as S_Statement_ResolveConfig_FN derives it:
 *  the first kind rental plan covering `month` (yyyy-mm) - enabled, the last day of its last month (none
 *  when open-ended), its id. Null when none covers the month. */
export function rentalFromPlans(plans: PaymentPlan[] | undefined, month: string): (RentalConfig & { fromPlan?: string }) | null {
  for (const p of plans ?? []) {
    if (p?.kind !== "rental" || !p.start || p.monthly == null || month < p.start) continue;
    const byMonths = p.months != null ? addMonths(p.start, p.months - 1) : undefined;
    const last = [byMonths, p.endsAfter ?? undefined].filter((x): x is string => !!x).sort()[0];
    if (last && month > last) continue;
    const endDate = last ? `${last}-${String(new Date(Number(last.slice(0, 4)), Number(last.slice(5, 7)), 0).getDate()).padStart(2, "0")}` : undefined;
    return { enabled: true, ...(endDate ? { endDate } : {}), fromPlan: p.id };
  }
  return null;
}

/** A rental (kind rental): the monthly amount every month, the set-up fee with the first; for `months`,
 *  else until endsAfter, else for good (the preview then shows PLAN_OPEN_PREVIEW_MONTHS). The SQL bills
 *  none with a principal, interest, down payment or residual. */
function rentalSchedule(p: PaymentPlan, kpi?: Record<string, number>): PlanInstallment[] {
  const open = p.months == null;
  const n = p.months, su = p.setupFee ?? 0;
  if (p.monthly == null || p.principal != null || (p.interest?.ratePct ?? 0) !== 0 || (p.downPayment ?? 0) !== 0 || (p.residual ?? 0) !== 0) return [];
  if (!open && (!Number.isInteger(n) || n! < 1 || n! > PLAN_MAX_MONTHS)) return [];
  const out: PlanInstallment[] = [];
  for (let k = 1; k <= (open ? PLAN_OPEN_PREVIEW_MONTHS : n!); k++) {
    const month = addMonths(p.start, k - 1);
    if (p.endsAfter && month > p.endsAfter) break;
    const first = k === 1, last = !open && k === n;
    // an indexed rent (spec 26.9): the rent and what is left x the factor, to the cent; the set-up fee as is
    const f = planKpiFactor(p, month, kpi), rent = cents(cents(p.monthly) * f);
    out.push({ month, installment: k, of: open ? null : n!, principalPart: rent, interestPart: 0,
      downPayment: 0, setupFee: first ? su : 0, residual: 0,
      remainingAfter: open ? null : last ? 0 : cents(cents(p.monthly * (n! - k)) * f),
      amount: cents(cents(p.monthly + (first ? su : 0)) - cents(p.monthly) + rent),
      ...(p.kpiAdjust ? { kpiFactor: f } : {}) });
  }
  return out;
}

export interface RentalConfig {
  enabled: boolean;
  /** When the rent period begins. Combined with endDate this gives the
   *  partner-report ("PDF") a defined range over which rent is charged —
   *  customer convention is a fixed term like 36 or 48 months. Once the
   *  period closes, recurring deviceFees.kiosk still applies but the rent
   *  line drops out of the payout calc. */
  startDate?: string | null;
  endDate?: string | null;
  feeOverride?: number | null;
}

/** Partner identity — tax-registration info needed on the operator payout
 *  report. Lives on the OPERATOR layer; KIOSK inherits via the resolver so
 *  per-kiosk override is possible but uncommon.
 *
 *  Note: IBAN intentionally NOT included here — bank-account numbers are
 *  PII / financial-sensitive and shouldn't live in a versioned plaintext
 *  JSON blob. IBAN belongs on S_Owner_Operator (or a payout-info side
 *  table) with column-level encryption / restricted read permission.
 *  That's a separate change. */
export interface PartnerInfo {
  /** Organization number / VAT registration number printed on the statement. */
  vatNumber?: string;
  /** Our customer reference for this operator/reseller — replaces
   *  S_Owner_Operator.sExternalReference. Still written into the Odoo statistics
   *  records as x_operator_ref, but NOT used to find the partner: that lookup is
   *  gone (2026-08-27), because the field it searched
   *  (res.partner.customer_number) does not exist on the live instance. */
  odooRef?: string;
  /** Odoo **res.partner database id** for this operator/reseller, read out of Odoo
   *  and entered here. The processor sends it as the invoice's partner_id verbatim
   *  — no lookup, no fallback — so an invoice whose config has no id FAILS with a
   *  message naming this field, and a WRONG id bills the wrong customer with
   *  nothing to catch it. Verify with tools/Check-OdooPartner.ps1 -PartnerId. */
  odooId?: number | null;
}

/** Odoo connection profile — DATABASE-scope only. apiKey lives in the
 *  encrypted SensitiveData envelope, never in this plaintext block. */
export interface OdooProfile {
  type: "Odoo";
  /** Application-config Odoo target name. When set, the processor uses the
   *  appsettings `OdooTargets[target]` (connection + field mapping) as the base
   *  and treats the fields below as per-key overrides — so many databases share
   *  one target and a reseller can point at a different target / Odoo database.
   *  The encrypted apiKey always stays in this config, never in appsettings. */
  target?: string;
  /** Required when no target supplies it (validateConfig enforces this for
   *  DATABASE scope); optional here because a target can supply it instead —
   *  the server (ProfileOptions in Options.cs) defaults every one of these
   *  string fields to "" when absent, identically to an explicit "", so
   *  leaving a field unset is always safe when a target is set and neutral
   *  when it isn't. */
  baseUrl?: string;
  db?: string;
  journalCode?: string;
  accountCodes?: { payout?: string; fee?: string };
  statsModel?: string;
  dailyStatsModel?: string;
  statsRefField?: string;
  company?: string;
  activateCurrencies?: boolean;
  updateDraftDaily?: boolean;
  /** NOTE — the daily-close block that used to live here (odoo.bookKeeping: journal,
   *  clearingAccount, autoPost, VAT) moved to the config's OWN top-level `bookKeeping`
   *  key on 2026-08-27, joining the half that was already there (enabled, accounts).
   *  See BookKeepingConfig. The processor no longer reads it here and says so in the
   *  log if it finds one, so nothing should write it. */
  /** Odoo field each of our CODES is looked up by when translating to an Odoo
   *  database id (search the field for our code → take the row id). Every key is
   *  optional; the processor applies its default when unset. Only override when the
   *  Odoo instance maps a code via a non-standard field.
   *
   *  No product or partner entry (2026-08-27): those two named a per-customer
   *  CUSTOM field, and the live instance does not have it — res.partner has no
   *  customer_number and its rows carry no ref either. Both ids now come from here
   *  (partnerInfo.odooId, fees[].articleNumber.odooId) instead. What remains are
   *  standard Odoo columns present on every instance. */
  lookupFields?: {
    /** res.currency field for the ISO code. Default "name". */
    currency?: string;
    /** res.company field for the company name. Default "name". */
    company?: string;
    /** account.journal field for the journal code. Default "code". */
    journal?: string;
    /** account.account field for the account code. Default "code". */
    account?: string;
  };
}

/** One bookkeeping account mapping for a method group (spec contract 4). */
export interface BookKeepingAccount {
  /** Bookkeeping account code (chart of accounts), e.g. "3010". */
  act: string;
  /** income = revenue recognized directly on the operator's account;
   *  debt = intermediary liability (self-invoice payout later). */
  type: "income" | "debt";
}

/** Daily bookkeeping close config — top-level whole-key, DATABASE default +
 *  OPERATOR override (see keysForScope). Group membership (which processors
 *  count as IPA / C&P) is platform code (dbo.S_Statement_PaymentGroup_FN),
 *  NOT config — this only maps group → account; the group list itself comes
 *  from listBookKeepingGroups() so a new group shows up without a client
 *  change. */
export interface BookKeepingConfig {
  /** Default false: the daily bookkeeping rows are always produced for
   *  reference regardless of this flag; true = the statement processor also
   *  posts them to Odoo as journal entries. */
  enabled?: boolean;
  /** Keyed by method group name from WebStatementBookKeepingGroups (e.g.
   *  "IPA", "C&P"). Read by SQL as well as by the processor — this path may not move. */
  accounts?: Record<string, BookKeepingAccount>;

  /* ── moved here from odoo.bookKeeping on 2026-08-27 ────────────────────────
   * One key for one feature. `odoo` merges as a WHOLE key, so an operator pointing at
   * its own Odoo instance previously had to restate the entire ledger setup — journal,
   * clearing account, every VAT account — inside its own odoo block just to change a
   * base URL, and a half-copied posting configuration is a financial defect. Ledger
   * config now inherits on its own axis. Field names must match
   * StatementInvoiceProcessor's BookKeepingOptions (Options.cs) exactly. */

  /** account.journal code the entries post into. Required to push. */
  journal?: string;
  /** account.account code of the debit (PSP/bank clearing) side. Required to push;
   *  per-currency via accountsByCurrency. */
  clearingAccount?: string;
  /** Post the move right after create. Default true; false leaves drafts for
   *  accountant review (drafts are rebuilt when the row changes). */
  autoPost?: boolean;
  /** Debt (agent) entries: false (DEFAULT) = the tax rides inside the liability line,
   *  so the entry is only liability/clearing — the customer's accountant's rule, since
   *  money held for a client is not revenue we recognise VAT on. true = split ex-tax +
   *  VAT lines like an income entry, and then a VAT account is required. */
  recognizeVatOnDebt?: boolean;
  /** true = look up vatAccounts[rate] first, falling back to vatAccounts.default.
   *  false (default) = always the "default" account. */
  vatAccountPerRate?: boolean;
  /** account.account codes for VAT lines, keyed by invariant rate string
   *  ("12", "6", "25.5") plus the required "default" fallback. A
   *  needed-but-missing entry fails the push loud — never posts to a
   *  guessed account. */
  vatAccounts?: Record<string, string>;

  /** Per-currency chart of accounts: ISO code → (base account code → the code that
   *  currency uses). Applied to EVERY account the push resolves — clearing, liability
   *  and VAT — so one table covers all three roles.
   *
   *  An operator holding client money per currency has one account per currency
   *  ("2830 Kundskuld SEK" is not where a EUR liability belongs), while the group
   *  account map, the clearing account and the VAT accounts carry no currency
   *  dimension at all.
   *
   *  A currency absent here behaves exactly as before (identity — which is what the
   *  base currency wants). A currency that IS listed but lacks an account the push
   *  needs FAILS the push, naming account and currency: the alternative is quietly
   *  posting a client's money onto another currency's account. */
  accountsByCurrency?: Record<string, Record<string, string>>;

  /* ── The accrual leg ───────────────────────────────────────────────────────
   * Revenue recognised when the goods change hands rather than when the card settles.
   * A separate node from `accounts` because the two key different things: `accounts` answers
   * HOW THE MONEY ARRIVED (method group), `events` answers WHAT HAPPENED TO THE SALE.
   *
   * The two are mutually exclusive per operator. An operator running accrual keeps `accounts`
   * EMPTY: the accrual credits revenue at the transaction and the settled leg would credit it
   * again when the card clears, so a config carrying both books its revenue twice. The
   * processor refuses such a row rather than posting half of it. */

  /** What each occurrence POSTS, keyed by occurrence name. The close reports what happened; this
   *  decides the entry it becomes. An occurrence with no lines posts nothing and is reported
   *  instead, which is how a recovery is handled here.
   *
   *  Nothing about debits, credits or any one country's tax law is fixed in code: a deployment
   *  that reverses VAT on a loss and one that absorbs it into the cost differ only here. */
  postings?: Partial<Record<AccrualOccurrence, PostingLine[]>>;

  /** When a loss is confirmed. Separate from what it posts, because the two are independent
   *  choices — forfeit the VAT and there is nothing to wait for; reclaim it and a tax authority
   *  usually imposes conditions. */
  writeOff?: WriteOffRule;

  /** First day the accrual leg may post, `YYYY-MM-DD`. Earlier days are still generated and still
   *  readable, they simply never reach Odoo — the periods before go-live were booked by hand, and
   *  a backfill must not post into a month the accountant already entered. */
  postFromDate?: string;

  /** Name the accrual move carries in Odoo, `{date}` substituted, e.g.
   *  "Försäljning Instant Cooler {date}". NOT the idempotency key — that is the deterministic
   *  ref, so changing this never changes which move a ref resolves to. */
  accrualMoveLabel?: string;

  /** `MM-DD` the financial year closes. Reported alongside a recovery so the reconciliation can
   *  tell a same-year reversal from a later-year recovered receivable. Default `12-31`. */
  fiscalYearEnd?: string;
}

/** What the close can tell apart. Platform truth, fixed by S_Statement_AccrualClose_SP — a
 *  deployment chooses what each one POSTS, never what the occurrences are. */
export const ACCRUAL_OCCURRENCES = [
  "sale", "refund", "lost", "captureShortfall", "recovered",
] as const;
export type AccrualOccurrence = (typeof ACCRUAL_OCCURRENCES)[number];

/** One line of an occurrence's posting rule. */
export interface PostingLine {
  /** account.account code. */
  act: string;
  side: "debit" | "credit";
  /** Which amount this line takes: `net` excludes VAT, `vat` is the tax alone, `gross` is both. */
  amount: "net" | "vat" | "gross";
  /** Set to take one VAT rate's share and post it to a rate-specific account. Absent = the
   *  occurrence total. */
  taxRate?: number;
}

export interface WriteOffRule {
  /** `onLost` (default) confirms the loss when it is flagged — right wherever the VAT is
   *  forfeited, since nothing is being reclaimed and no condition applies. `aged` waits, for the
   *  case where the VAT IS reclaimed and a tax authority imposes conditions. */
  trigger?: "onLost" | "aged";
  /** `aged` only: months from the date payment fell due. Sweden requires 8. */
  afterMonths?: number;
  /** `aged` only: gross amount below which the simplified route applies. Sweden uses 1000 kr. */
  maxAmount?: number;
}


export interface StatementConfigData {
  aggregationPeriod?: "Daily" | "Weekly" | "BiWeekly" | "SemiMonthly" | "Monthly";
  /** WholeWeek = weeks never split (approx-month invoices); WeekWithinMonth =
   *  split at month boundary for exact-calendar-month invoices. */
  aggregationStrategy?: "WholeWeek" | "WeekWithinMonth";
  /** Billing cadence (bundles aggregates into one invoice). Operator-grained. */
  invoicePeriod?: "Weekly" | "BiWeekly" | "SemiMonthly" | "Monthly";
  /** Which documents a period produces. TWO independent questions and one policy, stored as one
   *  code so a layered config can never assemble a cell nobody chose:
   *
   *  - `Fees` - customer invoice for the fees alone, no payout.
   *  - `PayoutOnly` - self-billed vendor bill for what we collected, no fee lines.
   *  - `Both` (default) - ONE netting document: the collection with the fees as negative lines.
   *  - `BothSplit` - the fee invoice AND the payout bill. More reconciliation (Odoo does not
   *    offset AR against AP by itself) in exchange for fee VAT on a document that can report it;
   *    on a netting vendor bill our fees are negative lines, which VAT reporting reads as
   *    negative input tax.
   *
   *  Operator-grained. `Payout` and `FeeInvoice` are the pre-2026-08-31 names for `Both` and
   *  `Fees`; they are still accepted everywhere and normalised at invoice generation, since rows
   *  already carry them. `StrictPayout`, removed the same day, is REFUSED with its migration
   *  (it meant `PayoutOnly`, except that it paid out a fee). */
  billingMode?: "Fees" | "PayoutOnly" | "Both" | "BothSplit" | "Payout" | "FeeInvoice";
  /** Payout mode only: may a period whose fees exceed the collection turn into a customer
   *  INVOICE for the difference?
   *
   *  true (default) - the money runs the other way, so the document flips: a customer invoice
   *  with every sign inverted. false - the fees are capped at the collection, so the bill can
   *  only ever pay the operator or pay nothing; every fee line keeps its full amount and one
   *  explicit line gives the uncollected part back. The shortfall is absorbed, never carried
   *  into the next period. false is what `billingMode: StrictPayout` used to mean.
   *
   *  Read through `configBool`: the resolver stringifies merged top-level scalars, so this
   *  arrives as `false` at DATABASE scope and `"false"` once any layer is overlaid. */
  invoiceWhenFeesExceedPayout?: boolean;
  /** What to do when the COLLECTION ITSELF is negative - a period whose refunds outran its
   *  takings. Distinct from `invoiceWhenFeesExceedPayout`, which is about the fees outrunning a
   *  positive collection, and it applies to every payout-bearing shape.
   *
   *  - `skip` (default) - no document for that currency; the refunds net off the next period's
   *    collection by themselves, since the daily close accrues per day.
   *  - `creditNote` - draft the vendor bill with its negative total, which Odoo reads as a vendor
   *    credit note.
   *  - `invoice` - bill the operator: a customer invoice for the absolute amount. */
  negativePayout?: "skip" | "creditNote" | "invoice";
  /** May an invoice be APPROVED (and so posted in Odoo) from the Admin grid?
   *
   *  Default **false**: the accountant confirms the draft inside Odoo instead, and SIP records
   *  the invoice as posted when its next sweep sees that move. Viewing the draft is unaffected
   *  either way - this only governs the Approve action.
   *
   *  Captured onto each invoice row at generation (`S_Statement_Invoice.AllowApprovalInAdmin`),
   *  the same way `billingMode` is, so the grid's affordance and
   *  `S_Statement_ApprovePayout_SP`'s refusal always read one value. Flipping it therefore
   *  affects invoices generated AFTER the change, not ones already bundled. */
  allowApprovalInAdmin?: boolean;
  weekEndsOn?: string;
  biWeeklyParity?: "Even" | "Odd";
  semiMonthlySplitDay?: number;
  /** Days from period end to the invoice due date. Consumed SQL-side when the invoice row
   *  is generated (SIP reads the resulting DueDate, never this). Present in live configs
   *  since before this client had a field for it. */
  paymentTermDays?: number;
  /** DATABASE scope only. Who the invoice goes to: "database" (default) = one invoice per period
   *  for the whole database, addressed to its owner, every operator on it; "operator" = one per
   *  operator, operator 0 (the owner's own kiosks) included. Read from the DATABASE row by
   *  S_Statement_GenerateInvoice_SP. */
  invoiceTo?: InvoiceTo;
  /** DATABASE scope only. The customer name on a database invoice (default: the database name). */
  invoiceName?: string;
  /** "unverified" blocks invoicing for the WHOLE database (statements still close) until the
   *  setup is confirmed against the invoices raised by hand. Any scope; one row is enough. */
  billingStatus?: BillingStatus;
  /** Why it is unverified - shown in the GenerateInvoice error. */
  billingStatusReason?: string;
  /** The invoice's contact fields: Your reference (who to address at the customer, or a cost
   *  centre), Your order No, Our reference (our contact). Stored for the Odoo push; which Odoo
   *  fields they map to is not decided yet. */
  invoiceReferences?: InvoiceReferences;
  /** Root/billing currency. Also the fallback for ingoing/outgoing resolution. */
  currency?: string;
  /** Default currency the supplied fee values are in (per-fee override wins). */
  defaultIngoingCurrency?: string;
  /** Default currency fees are billed in (per-fee override wins). */
  defaultOutgoingCurrency?: string;
  /** THE fee schedule — every fee is one generic entry. */
  fees?: FeeEntry[];
  /** Daily bookkeeping close: per-method-group account mapping + ledger push
   *  gate. DATABASE default + OPERATOR override (see keysForScope). */
  bookKeeping?: BookKeepingConfig;
  /** Detail sheets attached to every invoice this config produces. Invoice-level,
   *  not fee-level: which document the customer receives has nothing to do with
   *  which fee happened to bill, and hanging it off a fee meant the sheet vanished
   *  whenever that fee calculated to zero. The processor still honours the old
   *  fees[].params.attachment and logs a nudge. */
  invoiceAttachments?: AttachmentType[];
  /** Yearly multiplier applied by fees with params.kpiAdjust = true. */
  kpiAdjustments?: Record<string, number>;
  rental?: RentalConfig;
  /** IANA zone for an ADDITIONAL informational daily-close row on the
   *  operator's local day (billing always stays Europe/Stockholm). */
  localReportingTimeZone?: string;
  partnerInfo?: PartnerInfo;
  odoo?: OdooProfile;
  /** OPERATOR-scope only: which reseller (if any) this operator chains through. */
  resellerId?: number;
  /** OPERATOR-scope only: the billing group platform invoicing bills this operator under (e.g. DK):
   *  published on its rows, and a billing account member can bill one group. */
  billingGroup?: string;
  /** OPERATOR-scope only: how the reseller chain composes the calc. */
  chainModel?: "Independent" | "Waterfall";
  /** DATABASE-scope only: the database owner invoices its own fees (S_Statement_GenerateInvoice_SP,
   *  today only Access_MaikozEU). Every fee then states params.layer: the platform fees are
   *  invoiced by Instant Systems from PayaP_Global, the owner fees by the owner. Read from the
   *  DATABASE row as written, not through the resolver. */
  ownerInvoicing?: boolean;
  /** DATABASE-scope only (for now): fixed payment schedules billed on the database row (spec 26). */
  paymentPlans?: PaymentPlan[];
  /** Waterfall only: net the inbound transaction-based (processor) fees into
   *  the OUTBOUND revenue-share base. Default true. */
  waterfallNetProcessorFee?: boolean;
}

/** Keys the SQL resolver (S_Statement_ResolveConfig_FN) injects into the
 *  returned config JSON alongside `_sources` (e.g. payoutInfo's public
 *  envelope). These are resolver artefacts, not real config — they must
 *  never round-trip into stored ConfigData, so every place that persists a
 *  resolved config back to the server strips them (in addition to the
 *  `_`-prefixed internal keys). */
export const RESOLVER_INJECTED_KEYS = ["payoutInfo", "odooSecret"] as const;

/** The resolver also stamps keys INSIDE a fee (`_kpiFactor` / `_kpiBaseYear` on a fee with
 *  params.kpiBaseYear). Stripped from every fee before a save: a stored stamp outlives the base
 *  year it was computed for, and the billing prefers it. */
export function stripFeeInternals(data: StatementConfigData): StatementConfigData {
  if (!Array.isArray(data.fees)) return data;
  return {
    ...data,
    fees: data.fees.map((f) =>
      f && typeof f === "object"
        ? (Object.fromEntries(Object.entries(f).filter(([k]) => !k.startsWith("_"))) as typeof f)
        : f),
  };
}

/** Plaintext payout payload — beneficiary + account + bank fields the
 *  partner-payout report needs. Posted on save (server encrypts before
 *  storing); returned by revealPayoutInfo() for editing. Never round-trips
 *  through normal reads. */
export interface PayoutInfo {
  beneficiaryName?: string;
  beneficiaryAddress?: string;
  iban?: string;
  bic?: string;
  bankgiro?: string;
  postgiro?: string;
  accountNumber?: string;
  bankName?: string;
  bankAddress?: string;
}

/** Public payout-info summary returned through normal reads. Sensitive
 *  account-number fields arrive pre-masked (last 4 only); IBAN keeps its
 *  bank-style mask (`DE28...2345`); beneficiary/bank identity + BIC are
 *  plaintext. */
export interface PayoutInfoSummary {
  beneficiaryName?: string;
  beneficiaryAddress?: string;
  iban?: string;
  bic?: string;
  bankgiro?: string;
  postgiro?: string;
  accountNumber?: string;
  bankName?: string;
  bankAddress?: string;
}

/** Public envelope. `keyId` indicates which encryption key was used
 *  (rotation support); `info` is the summary above. Full plaintext is only
 *  available via revealPayoutInfo(), gated on Write(statementMgmt). */
export interface PayoutInfoPublic {
  keyId: string;
  info: PayoutInfoSummary;
}

/** Public odoo-secret envelope returned through normal reads — only states
 *  whether an apiKey is set. Full plaintext via revealOdooSecret(). */
export interface OdooSecretPublic { keyId: string; info: { apiKeySet?: boolean }; }

export interface StatementConfigVersion {
  configID: number;
  scope: ScopeLayer;
  scopeID: number;
  scopeLabel: string;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  comment?: string;
  data: StatementConfigData;
  /** Public payout-info envelope when a payout payload is stored. */
  payoutInfo?: PayoutInfoPublic | null;
  odooSecret?: OdooSecretPublic | null;
  changedKeys?: string[];
  seed?: boolean;
}

export async function listVersions(scope: ScopeLayer, scopeID: number): Promise<StatementConfigVersion[]> {
  try {
    const res = await servicePost<ServerVersionDto[]>("WebStatementConfigList", { scope, scopeID });
    if (res.bError || !res.oData) return [];
    return res.oData.map(r => mapServerVersion(r, scope === "DATABASE" ? "" : String(r.ScopeID)));
  } catch {
    return [];
  }
}

export async function resolveConfig(
  scope: ScopeLayer, scopeID: number, asOf: string,
  resellerId = 0, tier?: string,
): Promise<{
  data: StatementConfigData;
  sources: Record<string, { layer: ScopeLayer; configID: number }>;
  payoutInfo: PayoutInfoPublic | null;
  odooSecret: OdooSecretPublic | null;
}> {
  const empty = {
    data: {} as StatementConfigData,
    sources: {} as Record<string, { layer: ScopeLayer; configID: number }>,
    payoutInfo: null as PayoutInfoPublic | null,
    odooSecret: null as OdooSecretPublic | null,
  };
  try {
    const res = await servicePost<string>("WebStatementResolveConfig", { scope, scopeID, asOf, resellerID: resellerId, tier });
    if (res.bError || !res.oData) return empty;
    let data: StatementConfigData = {};
    try { data = JSON.parse(res.oData); } catch { return empty; }
    // Server injects:
    //   _configID    — most-specific contributing layer's row ID
    //   _sources     — per-key { layer, configID } map
    //   payoutInfo   — public envelope ({keyId, info-summary}); stripped
    //                  out of `data` here so consumers never see it as
    //                  part of the saveable config.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyData = data as any;
    const fallbackConfigID = Number(anyData._configID ?? 0);
    const rawSources = (anyData._sources ?? {}) as Record<string, { layer?: string; configID?: number }>;
    const sources: Record<string, { layer: ScopeLayer; configID: number }> = {};
    for (const k of TOP_LEVEL_KEYS) {
      const src = rawSources[k];
      const layer = (src?.layer as ScopeLayer | undefined) ?? scope;
      const configID = Number(src?.configID ?? fallbackConfigID);
      sources[k] = { layer, configID };
    }
    const payoutSrc = rawSources["payoutInfo"];
    if (payoutSrc) {
      sources["payoutInfo"] = {
        layer: (payoutSrc.layer as ScopeLayer | undefined) ?? scope,
        configID: Number(payoutSrc.configID ?? fallbackConfigID),
      };
    }
    let payoutInfo: PayoutInfoPublic | null = null;
    const pi = anyData.payoutInfo;
    if (pi && typeof pi.info === "object" && pi.info !== null) {
      payoutInfo = { keyId: String(pi.keyId ?? ""), info: pi.info as PayoutInfoSummary };
    }
    // odooSecret rides along with whichever row's `odoo` block won resolution
    // (see sources.odoo, populated above via TOP_LEVEL_KEYS — the resolver
    // ties the two together since an API key authenticates against one
    // specific Odoo instance). Absent from the JSON entirely when the
    // winning odoo row has no secret, or no odoo block resolved at all.
    let odooSecret: OdooSecretPublic | null = null;
    const os = anyData.odooSecret;
    if (os && typeof os.info === "object" && os.info !== null) {
      odooSecret = { keyId: String(os.keyId ?? ""), info: os.info as OdooSecretPublic["info"] };
    }
    // Resolver-injected keys never belong in the returned `data` — they're
    // artefacts of the merge, not config a caller should ever save back.
    for (const k of RESOLVER_INJECTED_KEYS) delete anyData[k];
    return { data, sources, payoutInfo, odooSecret };
  } catch {
    return empty;
  }
}

export interface SaveArgs {
  scope: ScopeLayer;
  scopeID: number;
  validFrom: string;
  data: StatementConfigData;
  comment?: string;
  /** Optional plaintext payout-info struct. When present, server encrypts
   *  under the active key + writes a new envelope. When absent, the new
   *  version carries forward whatever payout payload the prior version
   *  had. */
  rawPayoutInfo?: PayoutInfo;
  /** When true, explicitly clears any inherited payout info on the new
   *  version. Use this to remove — leaving rawPayoutInfo empty just
   *  carry-forwards the prior value. */
  clearPayoutInfo?: boolean;
  /** Optional plaintext odoo secret ({ apiKey }). Server encrypts. Same
   *  carry-forward/clear contract as rawPayoutInfo. */
  rawOdooSecret?: { apiKey: string };
  clearOdooSecret?: boolean;
}
/** Field names that must be numeric in the stored ConfigData JSON. A bug
 *  upstream (clone flow, JSON paste, legacy data) once persisted strings like
 *  `"180.0"` for `enterpriseFee`; the SQL side then tolerated it silently
 *  because CAST(... AS DECIMAL) accepts both. Coerce-at-the-wire here so the
 *  shape on disk is always numeric regardless of upstream contamination. */
const NUMERIC_CONFIG_KEYS = new Set<string>([
  "semiMonthlySplitDay",
  "fee", "revenuePercent", "minPerTxn", "minPerKioskMonthly", "quantity",
  "from", "to", "percentage",
  "feeOverride",
]);

function coerceConfigNumerics(key: string, value: unknown): unknown {
  if (typeof value !== "string" || !NUMERIC_CONFIG_KEYS.has(key)) return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
}

export async function saveVersion(args: SaveArgs): Promise<{ configID: number }> {
  const res = await servicePost<{ Success: boolean; ConfigID: number; Message: string }>(
    "WebStatementConfigSave",
    {
      args: {
        Scope: args.scope,
        ScopeID: args.scopeID,
        ValidFrom: args.validFrom,
        // Every save path (editor, clone) comes through here: the fee stamps are stripped once.
        ConfigData: JSON.stringify(stripFeeInternals(args.data), coerceConfigNumerics),
        Comment: args.comment ?? "",
        RawPayoutInfo: args.rawPayoutInfo ? JSON.stringify(args.rawPayoutInfo) : null,
        ClearPayoutInfo: !!args.clearPayoutInfo,
        RawOdooSecret: args.rawOdooSecret ? JSON.stringify(args.rawOdooSecret) : null,
        ClearOdooSecret: !!args.clearOdooSecret,
      },
    },
  );
  if (res.bError || !res.oData?.Success) {
    throw new Error(res.sMessage ?? res.oData?.Message ?? "save failed");
  }
  return { configID: res.oData.ConfigID };
}

/** One finding from the server's pre-save pool analysis.
 *
 *  `mixedPool` — the ladder pool this config lands in would end up holding
 *  members that carry DIFFERENT ladders. S_Statement_Aggregate_SP refuses to
 *  bill that, but it only finds out at aggregation time, weeks later. It is
 *  deliberately not blocked at save either: moving a pool to a new ladder means
 *  saving its members one at a time, so the first member moved would always
 *  look like the broken one. Hence warn-and-confirm rather than reject.
 *
 *  `poolShift` — the quieter one: a pooled ladder runs on the COMBINED revenue of
 *  its members, so a config that joins or leaves a pool moves the fee for
 *  operators whose own config nobody touched. Invisible from the form, invisible
 *  in their version history, visible on their invoice.
 *
 *  `poolJoin` — informational: who this config shares a pooled ladder with,
 *  when membership is not changing. */
export interface PoolWarning {
  code: "mixedPool" | "poolShift" | "poolJoin";
  severity: "warning" | "info";
  poolKey: string;
  /** Comma-separated operator IDs in the pool, ascending. */
  operators: string;
  /** Distinct ladders in the pool — more than one is the mismatch. */
  ladders: number;
  members: number;
  /** True when the config being saved is itself a member of this pool. */
  isPending: boolean;
  /** Members this pool gains (+) or loses (-). On its own this says only that the
   *  pool changed size — NOT which way money moved. */
  memberDelta: number;
  /** Which way the remaining members' fees move. Server-derived from the ladder's
   *  SHAPE together with memberDelta: the same departure raises fees on a
   *  descending ladder and lowers them on an ascending one, so the delta alone
   *  cannot say. "unknown" when the ladder rises and falls, or size is unchanged. */
  direction: "up" | "down" | "unknown";
  message: string;
}

interface PoolWarningRow {
  Code: string; Severity: string; PoolKey: string; Operators: string;
  Ladders: number; Members: number; IsPending: boolean; MemberDelta: number;
  Direction: string; Message: string;
}

/** Ask the server what saving this config would do to its ladder pool.
 *
 *  Never throws and never rejects: a warning that cannot be computed must not
 *  stand between the user and a save. The aggregate still refuses to bill a
 *  mixed pool, so nothing is lost but the early notice. */
export async function fetchPoolWarnings(
  args: Pick<SaveArgs, "scope" | "scopeID" | "validFrom" | "data">,
): Promise<PoolWarning[]> {
  try {
    const res = await servicePost<PoolWarningRow[]>("WebStatementConfigWarnings", {
      args: {
        Scope: args.scope,
        ScopeID: args.scopeID,
        ValidFrom: args.validFrom,
        // The same payload a save would send (saveVersion strips the fee stamps too).
        ConfigData: JSON.stringify(stripFeeInternals(args.data), coerceConfigNumerics),
      },
    });
    if (res.bError || !Array.isArray(res.oData)) return [];
    return res.oData.map(r => ({
      code: r.Code === "mixedPool" ? "mixedPool" : r.Code === "poolShift" ? "poolShift" : "poolJoin",
      severity: r.Severity === "warning" ? "warning" : "info",
      poolKey: r.PoolKey,
      operators: r.Operators,
      ladders: r.Ladders,
      members: r.Members,
      isPending: !!r.IsPending,
      memberDelta: r.MemberDelta ?? 0,
      direction: r.Direction === "up" ? "up" : r.Direction === "down" ? "down" : "unknown",
      message: r.Message,
    } as PoolWarning));
  } catch {
    return [];
  }
}

export async function deactivateVersion(configID: number): Promise<void> {
  const res = await servicePost<boolean>("WebStatementConfigDeactivate", { configID });
  if (res.bError) throw new Error(res.sMessage ?? "deactivate failed");
}

/** Reveal the plaintext payout-info struct for a single saved version.
 *  Server gates on Write(statementMgmt) and logs every successful reveal
 *  with the configID. Returns an empty object when nothing is stored. */
export async function revealPayoutInfo(configID: number): Promise<PayoutInfo> {
  const res = await servicePost<string>("WebStatementRevealPayoutInfo", { configID });
  if (res.bError) throw new Error(res.sMessage ?? "reveal failed");
  const raw = res.oData ?? "";
  if (!raw) return {};
  try { return JSON.parse(raw) as PayoutInfo; }
  catch { return {}; }
}

/** Reveal the plaintext odoo secret for one version. Gated on
 *  Write(statementMgmt); logged server-side. Returns {} when none stored. */
export async function revealOdooSecret(configID: number): Promise<{ apiKey?: string }> {
  const res = await servicePost<string>("WebStatementRevealOdooSecret", { configID });
  if (res.bError) throw new Error(res.sMessage ?? "reveal failed");
  const raw = res.oData ?? "";
  if (!raw) return {};
  try { return JSON.parse(raw) as { apiKey?: string }; }
  catch { return {}; }
}

const CALC_METHODS: readonly CalcMethod[] =
  ["perKiosk", "perDeviceCount", "perTransaction", "revenuePercent", "revenueLadder", "perOperator"];

function validCcy(v: string | undefined | null): boolean {
  return v == null || /^[A-Z]{3}$/.test(v);
}

/** Digits only → a positive integer or undefined. The parse used by every input that
 *  takes a hand-typed Odoo database id (partnerInfo.odooId, fees[].articleNumber.odooId).
 *
 *  Why filter rather than validate-and-warn: the processor FAILS an invoice whose id is
 *  present but not a positive id (a mapped fee's revenue belongs on its product, so it
 *  will not quietly bill by name instead). Making a bad value unsaveable in the first
 *  place is cheaper than discovering it on a billing run. Leading zeros and stray spaces
 *  are stripped; "0" collapses to undefined, since 0 is not an Odoo id. */
export function odooIdOrUndefined(v: string): number | undefined {
  const digits = v.replace(/\D+/g, "");
  if (!digits) return undefined;
  const n = Number(digits);
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}

/** An Odoo id that is either absent (legitimate) or usable. Absent is NOT an error here:
 *  a DATABASE-scope config carries no partner, a config being drafted may not have the id
 *  yet, and the processor already fails the push loudly with the field named. */
export function validOdooId(v: number | null | undefined): boolean {
  return v == null || (Number.isSafeInteger(v) && v > 0);
}

/** Blocking client-side checks per the admin brief. Returns null-or-message;
 *  null = OK. Server is authoritative; this is the fast-feedback gate.
 *  `resolved`: the server-resolved config at this scope, for what only the DATABASE row states
 *  (ownerInvoicing) when saving below it. */
export function validateConfig(scope: ScopeLayer, data: StatementConfigData, resolved?: StatementConfigData | null): string | null {
  // DATABASE scope: aggregationPeriod must be present.
  if (scope === "DATABASE" && !data.aggregationPeriod) {
    return "Aggregation period is required on the Database scope.";
  }
  // Odoo partner id: only a PRESENT-but-unusable value is blocked (see validOdooId). A
  // clone or an imported config can carry one the field editor would never have produced.
  if (!validOdooId(data.partnerInfo?.odooId)) {
    return `Odoo partner id must be a positive whole number (got "${data.partnerInfo?.odooId}").`;
  }
  // Currency: ISO 4217 — exactly 3 uppercase letters.
  if (!validCcy(data.currency)) {
    return `Currency must be a 3-letter ISO 4217 code (got "${data.currency}").`;
  }
  // Number(...) not Number.isInteger(data.paymentTermDays) directly: S_Statement_ResolveConfig_FN
  // emits every merged top-level SCALAR as a JSON string once any layer is overlaid
  // ("paymentTermDays":"30"), and the editor's baseline is that resolved config — so a strict
  // type check here would refuse to save a config the resolver itself produced.
  if (data.invoiceTo != null) {
    if (!(INVOICE_TO as readonly string[]).includes(data.invoiceTo)) return `Invoice to must be "database" or "operator".`;
    if (scope !== "DATABASE") return "Invoice to is set on the database config only.";
  }
  if (data.invoiceName != null && scope !== "DATABASE") return "Invoice name is set on the database config only.";
  if (data.billingStatus != null && !(BILLING_STATUSES as readonly string[]).includes(data.billingStatus)) {
    return `Billing status must be "verified" or "unverified".`;
  }
  if (data.paymentTermDays != null && `${data.paymentTermDays}`.trim() !== "") {
    const days = Number(data.paymentTermDays);
    if (!Number.isInteger(days) || days < 0) {
      return `Payment terms must be a whole number of days (got "${data.paymentTermDays}").`;
    }
  }
  if (!validCcy(data.defaultIngoingCurrency))  return "Default ingoing currency must be a 3-letter ISO 4217 code.";
  if (!validCcy(data.defaultOutgoingCurrency)) return "Default outgoing currency must be a 3-letter ISO 4217 code.";
  // Owner invoicing: DATABASE only - GenerateInvoice and Publish read it from the DATABASE row, so
  // one set elsewhere looks configured and does nothing.
  // Below DATABASE the flag comes from the resolved config: Publish refuses the month for ANY fee or plan
  // line with no layer, whatever scope wrote it.
  const ownerInv = configBool((scope === "DATABASE" ? data.ownerInvoicing : resolved?.ownerInvoicing) as unknown);
  if (data.ownerInvoicing != null && scope !== "DATABASE") return "Owner invoicing is set on the DATABASE config only.";
  // Generic fees.
  const fees = data.fees ?? [];
  const seen = new Set<string>();
  for (let f = 0; f < fees.length; f++) {
    const fee = fees[f]!;
    const label = fee.name?.trim() ? `"${fee.name.trim()}"` : `#${f + 1}`;
    if (!fee.name?.trim())                      return `Fee ${label}: name is required.`;
    const nameKey = fee.name.trim().toLowerCase();
    if (seen.has(nameKey))                      return `Fee ${label}: duplicate name — fee names must be unique.`;
    seen.add(nameKey);
    if (!CALC_METHODS.includes(fee.calcMethod)) return `Fee ${label}: unknown calc method "${fee.calcMethod}".`;
    if (!validOdooId(fee.articleNumber?.odooId)) {
      return `Fee ${label}: Odoo product id must be a positive whole number (got "${fee.articleNumber?.odooId}") — clear it to bill this fee by name.`;
    }
    const p = fee.params ?? {};
    if (p.layer != null && !(FEE_LAYERS as readonly string[]).includes(p.layer)) {
      return `Fee ${label}: invoicing layer must be platform or owner.`;
    }
    // SIP refuses an invoice with a fee of no layer there, and Publish blocks the month: stop it at save,
    // at every scope (below DATABASE the flag is the resolved one).
    if (ownerInv === true && p.layer == null) {
      return `Fee ${label}: owner invoicing is on, so every fee states its invoicing layer - platform (Instant Systems invoices it) or owner.`;
    }
    if (!validCcy(p.ingoingCurrency))           return `Fee ${label}: ingoing currency must be a 3-letter ISO 4217 code.`;
    if (!validCcy(p.outgoingCurrency))          return `Fee ${label}: outgoing currency must be a 3-letter ISO 4217 code.`;
    // Only the two percentage methods read revenueBase. Rejecting it elsewhere is not
    // pedantry: set on a perKiosk fee it looks configured and does nothing at all.
    if (p.revenueBase != null) {
      if (p.revenueBase !== "net" && p.revenueBase !== "gross") {
        return `Fee ${label}: revenue base must be "net" or "gross".`;
      }
      if (fee.calcMethod !== "revenuePercent" && fee.calcMethod !== "perTransaction") {
        return `Fee ${label}: revenue base only applies to a % of revenue or per-transaction fee.`;
      }
    }
    // The close fails loud on an unknown basis (for every config in the database, not just this
    // one), so it is stopped here; on another method it would look configured and do nothing.
    if (p.chargeBasis != null) {
      if (!(CHARGE_BASES as readonly string[]).includes(p.chargeBasis)) {
        return `Fee ${label}: charge basis must be one of ${CHARGE_BASES.join(", ")}.`;
      }
      if (fee.calcMethod !== "perKiosk" && fee.calcMethod !== "perDeviceCount") {
        return `Fee ${label}: charge basis only applies to a per-kiosk or per-device fee.`;
      }
    }
    if (p.kpiBaseYear != null) {
      if (!p.kpiAdjust) return `Fee ${label}: KPI base year only applies with KPI adjust on.`;
      if (!Number.isInteger(p.kpiBaseYear) || p.kpiBaseYear < 1900 || p.kpiBaseYear > 2999) {
        return `Fee ${label}: KPI base year must be a year (e.g. 2025).`;
      }
    }
    if (p.processors != null) {
      if (fee.calcMethod !== "perTransaction") return `Fee ${label}: processors only applies to a per-transaction fee.`;
      if (!Array.isArray(p.processors) || p.processors.length === 0 || p.processors.some((x) => typeof x !== "string" || x.trim() === "")) {
        return `Fee ${label}: processors must be a non-empty list of processor patterns (e.g. PAYTER%).`;
      }
    }
    if (p.per != null) {
      if (fee.calcMethod !== "perOperator") return `Fee ${label}: "per" only applies to a per-operator fee.`;
      if (p.per !== "operator" && p.per !== "database") return `Fee ${label}: per must be "operator" or "database".`;
    }
    if (p.quantity != null) {
      if (fee.calcMethod !== "perOperator") return `Fee ${label}: quantity only applies to a per-operator fee.`;
      if (typeof p.quantity !== "number" || !Number.isFinite(p.quantity) || p.quantity < 0) return `Fee ${label}: quantity must be 0 or more.`;
    }
    if (fee.calcMethod === "perDeviceCount" && !p.deviceType) {
      return `Fee ${label}: device type is required for a per-device-count fee.`;
    }
    if (fee.calcMethod === "revenueLadder") {
      if (p.ladderGroup != null && (p.scope ?? "operator") !== "database") {
        return `Fee ${label}: ladder group only applies to a database-scoped ladder.`;
      }
      if ((p.scope ?? "operator") === "group") {
        if (!p.globalDatabase?.trim()) return `Fee ${label}: group-scoped ladder needs the global database name.`;
      } else {
        // operator AND database scope both run these steps locally, so both need them valid.
        // A database-scoped ladder without steps would pool a base and have nothing to charge.
        // Ladder steps: contiguous ascending, to > from, percentage in [0,100].
        const steps = p.ladderSteps ?? [];
        if (steps.length === 0) return `Fee ${label}: ladder needs at least one step.`;
        for (let i = 0; i < steps.length; i++) {
          const s = steps[i]!;
          if (!(s.to > s.from))            return `Fee ${label}, step ${i + 1}: "to" must be greater than "from".`;
          if (s.percentage < 0 || s.percentage > 100) return `Fee ${label}, step ${i + 1}: percentage must be 0–100.`;
          if (i > 0 && steps[i - 1]!.to !== s.from)   return `Fee ${label}, step ${i + 1}: "from" must match previous step's "to" (contiguous).`;
        }
      }
    } else if (fee.fee == null && fee.calcMethod !== "perTransaction") {
      return `Fee ${label}: amount is required.`;
    }
  }
  // billingMode: the removed value gets its own message, since "unknown value" would read as a
  // typo when it is in fact a config written against last week's vocabulary. Payout and
  // FeeInvoice stay legal: they are the old names for Both and Fees, they sit in live configs,
  // and generation normalises them.
  if (data.billingMode != null) {
    const mode = `${data.billingMode}`.trim();
    if (mode === "StrictPayout") {
      return 'Billing mode "StrictPayout" was removed \u2014 it meant a payout with no fee lines, '
        + 'which is now "charge fees" off with "pay out" on.';
    }
    const known = ["Fees", "PayoutOnly", "Both", "BothSplit", "Payout", "FeeInvoice"];
    if (!known.includes(mode)) return `Unknown billing mode "${data.billingMode}".`;
  }
  // negativePayout: a closed vocabulary, and the processor FAILS the invoice on anything else
  // rather than defaulting - a typo that quietly skipped a period would be found by the customer,
  // not by us.
  if (data.negativePayout != null && `${data.negativePayout}`.trim() !== "") {
    const p = `${data.negativePayout}`.trim();
    if (p !== "skip" && p !== "creditNote" && p !== "invoice") {
      return `"When the collection is negative" must be skip, creditNote or invoice (got "${data.negativePayout}").`;
    }
  }
  // invoiceWhenFeesExceedPayout: a boolean, or the resolver's stringified one. Anything else
  // would be read as true (the default) by both the processor and configBool, so an unnoticed
  // "0" or "no" here means an operator gets invoiced who was configured never to be.
  const shortfall = data.invoiceWhenFeesExceedPayout as unknown;
  if (shortfall != null && typeof shortfall !== "boolean") {
    const t = `${shortfall}`.trim().toLowerCase();
    if (t !== "true" && t !== "false") {
      return `"Invoice when fees exceed the payout" must be true or false (got "${shortfall}").`;
    }
  }
  // allowApprovalInAdmin: same tolerance and the same reason as the flag above - an
  // unrecognised value reads as its default, and here the default is the SAFE direction (no
  // approval from Admin), so a typo fails closed rather than open. Still refused, because a
  // config that says "0" plainly meant something.
  const adminApproval = data.allowApprovalInAdmin as unknown;
  if (adminApproval != null && typeof adminApproval !== "boolean") {
    const t = `${adminApproval}`.trim().toLowerCase();
    if (t !== "true" && t !== "false") {
      return `"Allow approval from Admin" must be true or false (got "${adminApproval}").`;
    }
  }
  // Invoice attachments: known types only, no duplicates.
  const atts = data.invoiceAttachments;
  if (atts) {
    if (!Array.isArray(atts)) return "Invoice attachments must be a list.";
    const seenAtt = new Set<string>();
    for (const a of atts) {
      if (!ATTACHMENT_TYPES.includes(a)) return `Unknown invoice attachment "${a}".`;
      if (seenAtt.has(a)) return `Invoice attachment "${a}" is listed twice.`;
      seenAtt.add(a);
    }
  }
  // KPI adjustments: year keys = 4 digits, values > 0.
  const kpi = data.kpiAdjustments;
  if (kpi) {
    for (const [year, mult] of Object.entries(kpi)) {
      if (!/^\d{4}$/.test(year))         return `KPI year "${year}" must be 4 digits.`;
      if (!(Number(mult) > 0))           return `KPI multiplier for ${year} must be greater than 0.`;
    }
  }
  // Payment plans: what S_Statement_PlanInstallment_FN would silently not bill is refused here.
  if (data.paymentPlans != null) {
    if (!PLAN_SCOPES.includes(scope)) return "Payment plans are set on the DATABASE, an OPERATOR or a KIOSK config only.";
    if (!Array.isArray(data.paymentPlans)) return "Payment plans must be a list.";
    const ids = new Set<string>();
    for (const [i, pl] of data.paymentPlans.entries()) {
      const label = pl?.name?.trim() ? `"${pl.name.trim()}"` : `#${i + 1}`;
      if (!pl?.id?.trim()) return `Payment plan ${label}: an id is required (it keys the schedule and its invoice lines).`;
      if (ids.has(pl.id.trim().toLowerCase())) return `Payment plan ${label}: id "${pl.id}" is used twice.`;
      ids.add(pl.id.trim().toLowerCase());
      if (!pl.name?.trim()) return `Payment plan ${label}: a name is required (it is the invoice line).`;
      if (pl.kind != null && !(PLAN_KINDS as readonly string[]).includes(pl.kind)) return `Payment plan ${label}: the kind is an instalment plan or a rental.`;
      const rental = pl.kind === "rental";
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(pl.start ?? "")) return `Payment plan ${label}: the first month must be yyyy-mm.`;
      if ((!rental || pl.months != null) && (!Number.isInteger(pl.months) || pl.months! < 1 || pl.months! > PLAN_MAX_MONTHS)) {
        return `Payment plan ${label}: the number of months must be a whole number from 1 to ${PLAN_MAX_MONTHS}${rental ? ", or empty for an open-ended rental" : ""}.`;
      }
      if (rental) {
        if (pl.monthly == null || pl.principal != null) return `Payment plan ${label}: a rental is a monthly amount.`;
        if (pl.interest != null || pl.downPayment || pl.residual) return `Payment plan ${label}: a rental has no interest, down payment or residual.`;
      }
      if ((pl.principal == null) === (pl.monthly == null)) return `Payment plan ${label}: give either a total (principal) or a monthly amount, not both.`;
      for (const [k, v] of [["principal", pl.principal], ["monthly", pl.monthly], ["down payment", pl.downPayment], ["set-up fee", pl.setupFee], ["residual", pl.residual]] as const) {
        if (v != null && !(Number(v) >= 0)) return `Payment plan ${label}: the ${k} must be 0 or more.`;
        // whole cents: a fraction of a cent rounds differently in the preview and on the invoice
        if (v != null && Math.abs(Number(v) * 100 - Math.round(Number(v) * 100)) > 1e-6) return `Payment plan ${label}: the ${k} must be in whole cents.`;
      }
      if (pl.interest != null) {
        if (pl.monthly != null) return `Payment plan ${label}: interest needs a total (principal), not a monthly amount.`;
        if (!(Number(pl.interest.ratePct) >= 0 && Number(pl.interest.ratePct) < 100)) return `Payment plan ${label}: the interest rate must be 0 to 100 percent.`;
        if (pl.interest.method != null && !(PLAN_INTEREST_METHODS as readonly string[]).includes(pl.interest.method)) {
          return `Payment plan ${label}: interest is annuity (equal payments) or straight (equal repayment).`;
        }
      }
      if (pl.principal != null && !(pl.principal - (pl.downPayment ?? 0) > (pl.residual ?? 0))) {
        return `Payment plan ${label}: the total less the down payment must be more than the residual.`;
      }
      if (pl.endsAfter != null && (!/^\d{4}-(0[1-9]|1[0-2])$/.test(pl.endsAfter) || pl.endsAfter < pl.start)) {
        return `Payment plan ${label}: the last month must be yyyy-mm, not before the first.`;
      }
      if (!validOdooId(pl.articleNumber?.odooId)) {
        return `Payment plan ${label}: Odoo product id must be a positive whole number (got "${pl.articleNumber?.odooId}") - clear it to bill the plan by name.`;
      }
      if (pl.layer != null && !(FEE_LAYERS as readonly string[]).includes(pl.layer)) return `Payment plan ${label}: invoicing layer must be platform or owner.`;
      if (pl.kpiAdjust != null && typeof pl.kpiAdjust !== "boolean") return `Payment plan ${label}: indexed is on or off.`;
      if (pl.kpiAdjust && !rental) return `Payment plan ${label}: only a rental is indexed - an instalment plan pays off a fixed amount.`;
      if (pl.kpiBaseYear != null && !pl.kpiAdjust) return `Payment plan ${label}: a KPI base year needs the rent indexed.`;
      if (pl.kpiBaseYear != null && !(Number.isInteger(pl.kpiBaseYear) && pl.kpiBaseYear >= 1900 && pl.kpiBaseYear <= 2999)) {
        return `Payment plan ${label}: the KPI base year must be a year (yyyy), or empty for the year of the first month.`;
      }
      if (pl.currency != null && !/^[A-Z]{3}$/.test(pl.currency)) return `Payment plan ${label}: the currency must be a 3-letter code (EUR, SEK), or empty for the database one.`;
      // rounding each regular instalment up can, on a small amount over many months, leave the last one negative
      if (planSchedule(pl).some(x => x.principalPart < 0)) return `Payment plan ${label}: the amount is too small to spread over ${pl.months} months in whole cents.`;
      if (ownerInv === true && pl.layer == null) return `Payment plan ${label}: owner invoicing is on, so it states its invoicing layer.`;
    }
    // A rental plan with an amount bills the rent itself; a fee gated by rentalActive bills it too once the
    // gate is on - and a KIOSK rental plan IS the gate there (spec 26.8), as an enabled old rental block is
    // anywhere. Refused, judged on the effective config (this row over the resolved one). A rental of
    // monthly 0 is the gate alone, so the gated fee bills the rent: allowed.
    const billingRental = data.paymentPlans.find(pl => pl?.kind === "rental" && Number(pl.monthly) > 0);
    const effRental = data.rental ?? resolved?.rental;
    const effFees = data.fees ?? resolved?.fees ?? [];
    const gateOn = effRental?.enabled === true || (scope === "KIOSK" && data.paymentPlans.some(pl => pl?.kind === "rental"));
    if (billingRental && gateOn && effFees.some(f => f?.params?.condition === "rentalActive")) {
      return `Payment plan "${billingRental.name}": a fee gated by rentalActive bills the rent too while this kiosk counts as rented - remove that fee, or make the rental monthly 0 (the gate alone).`;
    }
  }
  // Rental: when enabled with a fee override, endDate is required.
  if (data.rental?.enabled && data.rental.feeOverride != null && !data.rental.endDate) {
    return "Kiosk Rental: end date is required when fee override is set.";
  }
  // Odoo profile (DATABASE scope): required connection fields when a block exists
  // and it does NOT reference an application-config target (a target supplies the
  // connection + accounts; the inline fields are then optional overrides).
  if (scope === "DATABASE" && data.odoo && !data.odoo.target?.trim()) {
    const o = data.odoo;
    if (!o.baseUrl?.trim())               return "Odoo: base URL is required (or set a target).";
    if (!o.journalCode?.trim())           return "Odoo: journal code is required (or set a target).";
    if (!o.accountCodes?.payout?.trim())  return "Odoo: payout account code is required (or set a target).";
    if (!o.accountCodes?.fee?.trim())     return "Odoo: fee account code is required (or set a target).";
  }
  // Daily close. Only the checks that are safe on a SPARSE payload — see bookKeepingIssues.
  const bkErr = bookKeepingIssues(data).find(i => i.severity === "error");
  if (bkErr) return bkErr.message;
  return null;
}

/* ── Daily close: what the form can prove wrong before the ledger does ──────
 *
 * The accrual block had no validation at all, and it is the one block where a wrong answer
 * moves real money: an unbalanced verification is refused by Odoo, and a config carrying both
 * legs books its revenue twice. Both are decidable here, from the config alone, at the moment
 * the user types — so neither needs to wait for a nightly push to be discovered.
 *
 * Split in two on purpose. `bookKeepingIssues` only ever says "what is here is wrong", which
 * is safe on a sparse OPERATOR payload carrying three keys out of thirty, so the save gate can
 * run it. `bookKeepingGapIssues` says "something required is missing", which is only true of a
 * config that is complete — run it against the MERGED view or it fails an operator row for
 * inheriting a journal it was never going to restate. */

export interface ConfigIssue {
  /** Layout field id that renders the offending setting, so the form can jump to it. */
  field: string;
  severity: "error" | "warning";
  message: string;
}

const OCCURRENCE_LABEL: Record<AccrualOccurrence, string> = {
  sale: "Sale", refund: "Refund", lost: "Loss",
  captureShortfall: "Partial capture", recovered: "Recovery",
};

/** How much of the net amount and how much of the VAT one line moves, signed by side.
 *  gross is both halves, which is what lets a two-line loss rule balance against a
 *  four-line sale rule that split the tax out. */
function lineWeight(l: PostingLine): { net: number; vat: number } {
  const sign = l.side === "debit" ? 1 : -1;
  if (l.amount === "net")   return { net: sign, vat: 0 };
  if (l.amount === "vat")   return { net: 0, vat: sign };
  return { net: sign, vat: sign };
}

export type BalanceVerdict = "empty" | "balanced" | "unbalanced" | "unprovable";

export interface OccurrenceBalance {
  verdict: BalanceVerdict;
  /** Signed residuals; zero on both means the entry cancels. Debit-positive. */
  net: number;
  vat: number;
  /** A line pinned to one VAT rate takes an unknown share of the tax, so the totals
   *  cannot be summed — the entry may still be perfectly correct. */
  rateRestricted: boolean;
}

/** Whether an occurrence's lines cancel. An entry whose debits and credits differ is rejected
 *  by the ledger, so proving it here turns a nightly push failure into a red line in the form.
 *
 *  Rate-restricted lines make the sum unknowable rather than wrong: "credit 2611 vat @25" takes
 *  whatever share of the day's tax carried 25%, which no config can predict. Those report
 *  `unprovable` and are never flagged — a false accusation on the one shape the Swedish example
 *  uses would train people to ignore the check. */
export function occurrenceBalance(lines: PostingLine[] | undefined): OccurrenceBalance {
  const ls = (lines ?? []).filter(l => l.act.trim());
  if (ls.length === 0) return { verdict: "empty", net: 0, vat: 0, rateRestricted: false };
  const rateRestricted = ls.some(l => l.taxRate !== undefined);
  let net = 0, vat = 0;
  for (const l of ls) {
    const w = lineWeight(l);
    net += w.net; vat += w.vat;
  }
  if (rateRestricted) return { verdict: "unprovable", net, vat, rateRestricted };
  const ok = net === 0 && vat === 0;
  return { verdict: ok ? "balanced" : "unbalanced", net, vat, rateRestricted };
}

/** True once any occurrence carries a posting line — the switch between the settled leg
 *  (money in, by payment-method group) and the accrual leg (revenue at the sale). */
export function usesAccrual(bk: BookKeepingConfig | undefined): boolean {
  const pos = bk?.postings;
  if (!pos) return false;
  return ACCRUAL_OCCURRENCES.some(o => (pos[o] ?? []).some(l => l.act.trim()));
}

/** True once any payment-method group names an account — the settled leg. */
export function usesSettledAccounts(bk: BookKeepingConfig | undefined): boolean {
  return Object.values(bk?.accounts ?? {}).some(a => a?.act?.trim());
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_DAY = /^\d{2}-\d{2}$/;

/** Problems with what the config SAYS. Safe on a sparse payload: every check reads a value
 *  that is present and judges it, so a key left to inherit is never the subject of one. */
export function bookKeepingIssues(data: StatementConfigData): ConfigIssue[] {
  const bk = data.bookKeeping;
  if (!bk) return [];
  const out: ConfigIssue[] = [];
  const err = (message: string) => out.push({ field: "bookKeeping", severity: "error", message });
  const warn = (message: string) => out.push({ field: "bookKeeping", severity: "warning", message });

  // The double-booking rule. The accrual credits revenue when the goods change hands and the
  // settled leg credits it again when the card clears; a row carrying both is refused by the
  // processor, so catching it here is the difference between a red field and a silent gap in
  // a month's ledger.
  if (usesAccrual(bk) && usesSettledAccounts(bk)) {
    const groups = Object.entries(bk.accounts ?? {}).filter(([, a]) => a?.act?.trim()).map(([g]) => g);
    err(`Daily close: revenue would be booked twice — the accrual posts it at the sale and the `
      + `payment-method account${groups.length === 1 ? "" : "s"} (${groups.join(", ")}) post `
      + `${groups.length === 1 ? "it" : "it"} again at settlement. Clear one side.`);
  }

  for (const occ of ACCRUAL_OCCURRENCES) {
    const lines = bk.postings?.[occ] ?? [];
    if (lines.length === 0) continue;
    const label = OCCURRENCE_LABEL[occ];

    const blank = lines.filter(l => !l.act.trim()).length;
    if (blank > 0) {
      warn(`${label}: ${blank} line${blank === 1 ? " has" : "s have"} no account and will be dropped when saved.`);
    }

    const seen = new Set<string>();
    for (const l of lines) {
      if (!l.act.trim()) continue;
      const key = `${l.side}|${l.act.trim()}|${l.amount}|${l.taxRate ?? ""}`;
      if (seen.has(key)) {
        err(`${label}: the same line appears twice (${l.side} ${l.act.trim()} ${l.amount}`
          + `${l.taxRate === undefined ? "" : ` @${l.taxRate}%`}) — it would post the amount twice.`);
      }
      seen.add(key);
      if (l.taxRate !== undefined && (!(l.taxRate >= 0) || l.taxRate > 100)) {
        err(`${label}: VAT rate must be between 0 and 100 (got "${l.taxRate}").`);
      }
    }

    const bal = occurrenceBalance(lines);
    if (bal.verdict === "unbalanced") {
      const parts: string[] = [];
      if (bal.net !== 0) parts.push(`${bal.net > 0 ? "debit" : "credit"} side takes the net amount ${Math.abs(bal.net)} time${Math.abs(bal.net) === 1 ? "" : "s"} too often`);
      if (bal.vat !== 0) parts.push(`${bal.vat > 0 ? "debit" : "credit"} side takes the VAT ${Math.abs(bal.vat)} time${Math.abs(bal.vat) === 1 ? "" : "s"} too often`);
      err(`${label}: debits and credits do not cancel — ${parts.join("; ")}. The ledger rejects an unbalanced entry.`);
    }
  }

  const wo = bk.writeOff;
  if (wo?.trigger === "aged") {
    if (wo.afterMonths !== undefined && !(Number(wo.afterMonths) > 0)) {
      err(`Confirm loss: the waiting period must be more than zero months (got "${wo.afterMonths}").`);
    }
    if (wo.maxAmount !== undefined && !(Number(wo.maxAmount) > 0)) {
      err(`Confirm loss: the amount threshold must be more than zero (got "${wo.maxAmount}").`);
    }
  }

  if (bk.postFromDate != null && `${bk.postFromDate}`.trim() !== "") {
    const v = `${bk.postFromDate}`.trim();
    if (!ISO_DATE.test(v) || Number.isNaN(Date.parse(v))) {
      err(`Post from date must be YYYY-MM-DD (got "${v}").`);
    }
  }
  if (bk.fiscalYearEnd != null && `${bk.fiscalYearEnd}`.trim() !== "") {
    const v = `${bk.fiscalYearEnd}`.trim();
    const mm = Number(v.slice(0, 2)), dd = Number(v.slice(3));
    if (!MONTH_DAY.test(v) || mm < 1 || mm > 12 || dd < 1 || dd > 31) {
      err(`Financial year end must be MM-DD (got "${v}").`);
    }
  }

  for (const [ccy, map] of Object.entries(bk.accountsByCurrency ?? {})) {
    if (!/^[A-Z]{3}$/.test(ccy)) {
      err(`Per-currency accounts: "${ccy}" is not a 3-letter ISO 4217 code.`);
    }
    for (const [from, to] of Object.entries(map ?? {})) {
      if (!`${to}`.trim()) warn(`Per-currency accounts: ${ccy} maps ${from} to nothing — the row does nothing.`);
    }
  }

  return out;
}

/** Problems with what the config LEAVES OUT. Only meaningful against a complete (merged)
 *  config — on a sparse payload every one of these fires for a key that inherits fine. */
export function bookKeepingGapIssues(bk: BookKeepingConfig | undefined): ConfigIssue[] {
  if (!bk?.enabled) return [];
  const out: ConfigIssue[] = [];
  const err = (message: string) => out.push({ field: "bookKeeping", severity: "error", message });

  if (!bk.journal?.trim())         err("Push to ledger is on, but no ledger journal is set — nothing can post.");
  if (!bk.clearingAccount?.trim()) err("Push to ledger is on, but no clearing account is set — every entry needs its debit side.");

  // A VAT line needs somewhere to go. Income groups always split the tax out; debt groups only
  // do when recognizeVatOnDebt is on. Either way the push fails on a missing default rather
  // than guessing an account, so the gap belongs in front of the user now.
  const hasIncome = Object.values(bk.accounts ?? {}).some(a => a?.act?.trim() && a.type !== "debt");
  const hasDebt   = Object.values(bk.accounts ?? {}).some(a => a?.act?.trim() && a.type === "debt");
  const needsVat  = hasIncome || (hasDebt && !!bk.recognizeVatOnDebt);
  if (needsVat && !bk.vatAccounts?.default?.trim()) {
    err("A default VAT account is required: "
      + (hasIncome ? "income groups split the tax onto their own line" : "VAT on debt is recognised")
      + ", and the push fails rather than guessing an account.");
  }
  return out;
}

export interface SampleCalcArgs {
  scope: ScopeLayer; scopeID: number; asOf: string;
  sampleRevenue: number; kioskCount: number;
  /** Optional what-if config. When provided, the server skips its resolver
   *  and uses this JSON verbatim — lets the form preview unsaved edits
   *  without persisting them as a real version first. */
  configOverride?: StatementConfigData;
  /** Reseller this operator chains through (>0 enables the 3rd-set summary). */
  resellerId?: number;
  /** Chain composition model ("Independent" | "Waterfall"). */
  chainModel?: string;
}
export interface SampleCalcResult {
  lines: { line: string; calc: string; amount: number }[];
  total: number;
  pctOfRevenue: number;
  /** 3rd-set chain summary — present only when scope OPERATOR + resellerId>0. */
  outboundTotal?: number;
  inboundTotal?: number;
  margin?: number;
}
interface ServerSampleCalcLine { Line: string; Calculation: string; Amount: number; }
interface ServerSampleCalcResult {
  Lines: ServerSampleCalcLine[];
  Total: number;
  PctOfRevenue: number;
  OutboundTotal?: number | null;
  InboundTotal?: number | null;
  Margin?: number | null;
}

export async function sampleCalc(args: SampleCalcArgs): Promise<SampleCalcResult> {
  const empty: SampleCalcResult = { lines: [], total: 0, pctOfRevenue: 0 };
  try {
    const res = await servicePost<ServerSampleCalcResult>("WebStatementSampleCalc", {
      args: {
        Scope: args.scope,
        ScopeID: args.scopeID,
        AsOfDate: args.asOf,
        SampleRevenue: args.sampleRevenue,
        KioskCount: args.kioskCount,
        ConfigOverride: args.configOverride ? JSON.stringify(args.configOverride) : null,
        ResellerID: args.resellerId ?? 0,
        ChainModel: args.chainModel ?? null,
      },
    });
    if (res.bError || !res.oData) return empty;
    const result: SampleCalcResult = {
      lines: (res.oData.Lines ?? []).map(l => ({ line: l.Line, calc: l.Calculation, amount: l.Amount })),
      total: res.oData.Total,
      pctOfRevenue: res.oData.PctOfRevenue,
    };
    // 3rd-set summary — null/absent unless scope OPERATOR + resellerId>0.
    if (res.oData.OutboundTotal != null) result.outboundTotal = res.oData.OutboundTotal;
    if (res.oData.InboundTotal != null) result.inboundTotal = res.oData.InboundTotal;
    if (res.oData.Margin != null) result.margin = res.oData.Margin;
    return result;
  } catch {
    return empty;
  }
}

// ── Statement invoice (statementAggregate → Invoice Number popup) ──────────

export interface StatementInvoice {
  ID: number;
  InvoiceNr: string;
  OperatorID: number;
  OperatorName: string;
  /** DIRECT | OUTBOUND | INBOUND — the invoice's tier relative to the reseller chain. */
  Tier?: string;
  /** Set when the invoice belongs to a reseller chain; for INBOUND the reseller is the real customer. */
  ResellerID?: number;
  ResellerName?: string;
  /** Payout | FeeInvoice. */
  BillingMode: string | null;
  /** Open (period forming) | Closed (period elapsed, approvable). */
  State: string | null;
  /** {By,At} once approved; null until then. */
  ApprovedJson: string | null;
  PeriodType: string;
  PeriodStart: string;
  PeriodEnd: string;
  /** Per-currency breakdown JSON derived from the linked aggregates
   *  ([{currency, revenueShareAmount, totalFee}]). */
  BreakdownJson: string | null;
  DueDate: string;
  PaidDate: string | null;
  PaidAmount: number | null;
  PaymentRefNr: string | null;
  CreatedAt: string;
  /** External-sync state JSON ({Method,Status,MoveIds,At,Error}) or null = not synced. */
  SyncJson: string | null;
}

export async function getStatementInvoice(invoiceID: number): Promise<StatementInvoice | null> {
  try {
    const res = await servicePost<StatementInvoice>("WebStatementInvoiceGet", { invoiceID });
    if (res.bError || !res.oData) return null;
    return res.oData;
  } catch {
    return null;
  }
}

/** The grid a detail sheet hangs off. Must match the page names the server asserts read
 *  permission on (`GetStatementSheetAPermission` → `SafeHasReadPermission(ctx, args.Name)`). */
export type StatementSheetPage = "statementInvoice" | "statementBookKeeping";

/**
 * Download one statement detail sheet for a selected invoice or bookkeeping close.
 *
 * The file is the SAME one StatementInvoiceProcessor attaches to the Odoo move — same library,
 * same stored JSON, same exporter, same export arguments — so this is the evidence the customer
 * already holds rather than a second opinion about it. Offered even when the config never asked
 * for the sheet, and for a period that was never pushed: "what would have been generated".
 *
 * Returns a FINISHED export, so `DownloadPath` is ready to fetch on return — no polling, unlike
 * `exportDataGrid`. A detail sheet is one invoice's worth of already-stored JSON, so the round
 * trip is the whole operation.
 *
 * No culture and no timezone, by design: a sheet is rendered invariant, always. Two files with
 * the same name and the same figures differing only in decimal separators would be worse than
 * having no choice.
 */
export async function getStatementSheet(args: {
  Name: StatementSheetPage;
  Sheet: StatementSheetType;
  ItemID: number;
  FileType: "XLSX" | "CSV";
}): Promise<ReturnObject<ExportInfo>> {
  return servicePost<ExportInfo>("GetStatementSheetA", { args });
}

// approveStatementInvoice removed — approval no longer has a dedicated method.
// The statementInvoice grid's `Approved` cell routes through UpdateGridCell →
// Web_Admin_Page_Data.AlternateUpdate → S_Statement_ApprovePayout_SP (see
// statementInvoiceGridOverrides.tsx). The SP enforces operator scope itself.

// getAuditLog removed — the audit page renders via the server-driven DataGrid
// (named grid `statementmgmtaudit` over S_Statement_Audit_View).

/** Top-level config keys — must match the merge-key set in
 *  S_Statement_ResolveConfig_FN (a key outside this set never survives a
 *  layered resolve). */
export const TOP_LEVEL_KEYS = [
  "aggregationPeriod","aggregationStrategy","invoicePeriod","billingMode","invoiceWhenFeesExceedPayout","negativePayout","allowApprovalInAdmin",
  "weekEndsOn","biWeeklyParity","semiMonthlySplitDay",
  // paymentTermDays: added 2026-08-27 so the editor can see and save it at all — this list
  // is the candidate set for the dirty-diff AND the save payload, so a key missing here is
  // a key the editor silently drops. VERIFY IT IS IN THE FN'S MERGE SET: if the resolver
  // builds its output from a fixed key list, an unlisted key vanishes from the resolved
  // config, and an OPERATOR-scope override of it would look saved and do nothing.
  "paymentTermDays",
  // Who is invoiced and whether billing may run. NOT in the resolver merge set on purpose:
  // S_Statement_GenerateInvoice_SP reads invoiceTo / invoiceName from the DATABASE row and
  // billingStatus from every active row, so a value set anywhere takes effect as written.
  "invoiceTo","invoiceName","billingStatus","billingStatusReason","invoiceReferences",
  // billingGroup: NOT in the resolver merge set either - S_Statement_Publish_SP reads it from the
  // OPERATOR row valid on the month end, as written.
  "billingGroup",
  // ownerInvoicing: DATABASE only, read from the DATABASE row as written (GenerateInvoice, Publish,
  // SIP). Missing here, a DATABASE save in the editor silently dropped it.
  "ownerInvoicing",
  // paymentPlans: DATABASE only, read from the DATABASE row as written by S_Statement_Aggregate_SP
  // (no inheritance, not in the resolver merge set).
  "paymentPlans",
  "currency","defaultIngoingCurrency","defaultOutgoingCurrency",
  "fees","invoiceAttachments","kpiAdjustments","rental","localReportingTimeZone",
  "partnerInfo","odoo","bookKeeping",
  "resellerId","chainModel","waterfallNetProcessorFee",
] as const;

/* ── Fee templates — the pre-generic fee set as one-click defaults ─────────
 * Mirrors S_Statement_SeedConfig_SP so "Add fee" offers the familiar options
 * with their conventional amounts; every value is editable after adding. */
export interface FeeTemplate { label: string; blurb: string; make: () => FeeEntry; }
export const FEE_TEMPLATES: FeeTemplate[] = [
  { label: "Kiosk device fee", blurb: "Flat monthly per kiosk (KPI-adjusted)",
    make: () => ({ name: "Kiosk device fee", calcMethod: "perKiosk", fee: 12, articleNumber: { odoo: null }, params: { kpiAdjust: true } }) },
  { label: "Device fee (per device)", blurb: "Monthly × device count (iqBox/sgBox/smartBox)",
    make: () => ({ name: "IqBox device fee", calcMethod: "perDeviceCount", fee: 10, articleNumber: { odoo: null }, params: { deviceType: "iqBox", kpiAdjust: true } }) },
  { label: "EMV fee", blurb: "Monthly per kiosk, only when EMV present",
    make: () => ({ name: "EMV fee", calcMethod: "perKiosk", fee: 7.5, articleNumber: { odoo: null }, params: { condition: "emvPresent" } }) },
  { label: "Fixed rate fee", blurb: "Flat monthly per kiosk",
    make: () => ({ name: "Fixed rate fee", calcMethod: "perKiosk", fee: 70, articleNumber: { odoo: null }, params: {} }) },
  { label: "Enterprise fee", blurb: "Flat monthly per kiosk (enterprise schedule)",
    make: () => ({ name: "Enterprise fee", calcMethod: "perKiosk", fee: 250, articleNumber: { odoo: null }, params: {} }) },
  { label: "Rental fee", blurb: "Monthly per kiosk while the rental period is active",
    make: () => ({ name: "Rental fee", calcMethod: "perKiosk", fee: 25, articleNumber: { odoo: null }, params: { condition: "rentalActive" } }) },
  { label: "Processor per-transaction", blurb: "Fixed + % per settled receipt with a floor",
    make: () => ({ name: "Processor per-transaction", calcMethod: "perTransaction", fee: 0.1, articleNumber: { odoo: null }, params: { revenuePercent: 0.9, minPerTxn: 0.15 } }) },
  { label: "Processor tech fee", blurb: "% of revenue with a monthly per-kiosk floor",
    make: () => ({ name: "Processor tech fee", calcMethod: "revenuePercent", fee: 1.5, articleNumber: { odoo: null }, params: { minPerKioskMonthly: 10 } }) },
  { label: "Revenue share (ladder)", blurb: "Tiered % on period revenue",
    make: () => ({ name: "Revenue share", calcMethod: "revenueLadder", fee: null, articleNumber: { odoo: null },
      params: { ladderSteps: [{ from: 0, to: 100000, percentage: 10 }, { from: 100000, to: 1e13, percentage: 5 }] } }) },
  { label: "Group revenue share", blurb: "Ladder computed on the group's GLOBAL database",
    make: () => ({ name: "Group revenue share", calcMethod: "revenueLadder", fee: null, articleNumber: { odoo: null },
      params: { scope: "group", globalDatabase: "" } }) },
  { label: "Per-operator fee", blurb: "Flat once per operator per period (e.g. VNC repeater)",
    make: () => ({ name: "VNC repeater fee", calcMethod: "perOperator", fee: 12, articleNumber: { odoo: null }, params: {} }) },
  { label: "Custom fee", blurb: "Start from a blank entry",
    make: () => ({ name: "", calcMethod: "perKiosk", fee: 0, articleNumber: { odoo: null }, params: {} }) },
];

/** Keys that only make sense at DATABASE/OPERATOR scope (billing is operator-grained,
 *  never per-kiosk). The statementMgmt editor hides these at KIOSK scope. Storage is
 *  unrestricted; this only filters what the UI surfaces.
 *  bookKeeping shares this exact grain (design: "DATABASE default + OPERATOR
 *  override", the daily close is per-operator, never per-kiosk) — hidden at
 *  KIOSK the same way; it also stays visible at RESELLER/RESELLER_INBOUND via
 *  this list, same as invoicePeriod/billingMode already do, since there is no
 *  narrower "DATABASE+OPERATOR only" filter today. */
export const OPERATOR_LEVEL_KEYS = ["invoicePeriod", "billingMode", "invoiceWhenFeesExceedPayout", "negativePayout", "allowApprovalInAdmin", "bookKeeping"] as const;

/** The `odoo` whole-key (connection profile + the API-key field). Originally
 *  DATABASE-only (deployment-wide profile); also surfaced at RESELLER (a
 *  reseller carries its own identity/payout + odoo profile) and OPERATOR (an
 *  operator can point at its own Odoo instance with its own key — the key
 *  travels with whichever odoo block it authenticates against). Never at
 *  KIOSK (no stated need — another place to leak/rotate a credential) or
 *  RESELLER_INBOUND (inbound schedules carry no identity of their own). */
export const ODOO_PROFILE_KEYS = ["odoo"] as const;

/** Scopes at which the `odoo` profile — and therefore its API key — may be edited.
 *
 *  SINGLE SOURCE OF TRUTH, and it needs to stay that way. This rule used to be written
 *  twice: once in `keysForScope()` below, once inline in formPane's `odoo` renderer.
 *  Adding OPERATOR meant editing both, and missing either one yields a scope where the
 *  key appears settable but renders nothing, or renders but is stripped on save. Anything
 *  deciding whether odoo is available at a scope MUST call `scopeAllowsOdoo` rather than
 *  re-testing the scope string. */
export const ODOO_SCOPES: readonly ScopeLayer[] = ["DATABASE", "RESELLER", "OPERATOR"];

export function scopeAllowsOdoo(scope: ScopeLayer): boolean {
  return ODOO_SCOPES.includes(scope);
}

/** Operator-only keys: the reseller chain (which reseller the operator chains
 *  through + how it composes + the waterfall netting gate) and the local
 *  reporting time zone (the daily-close sweep reads it from OPERATOR-scope
 *  configs only). */
export const RESELLER_CHAIN_KEYS = ["resellerId", "chainModel", "waterfallNetProcessorFee", "localReportingTimeZone"] as const;

/** Keys only the DATABASE config carries. */
export const DATABASE_ONLY_KEYS = ["ownerInvoicing"] as const;
/** Payment plans live at DATABASE, OPERATOR and KIOSK (spec 26.7, 26.8): each scope bills its own,
 *  a kiosk's on the row of the operator that owns it. Never on a reseller scope. */
export const PLAN_SCOPES: readonly ScopeLayer[] = ["DATABASE", "OPERATOR", "KIOSK"];
export const PLAN_KEYS = ["paymentPlans"] as const;

export function keysForScope(scope: ScopeLayer): readonly string[] {
  let keys: readonly string[] = TOP_LEVEL_KEYS;
  if (scope !== "DATABASE") keys = keys.filter(k => !(DATABASE_ONLY_KEYS as readonly string[]).includes(k));
  if (!PLAN_SCOPES.includes(scope)) keys = keys.filter(k => !(PLAN_KEYS as readonly string[]).includes(k));
  // Operator-only keys: OPERATOR only.
  keys = keys.filter(k => scope === "OPERATOR" || !(RESELLER_CHAIN_KEYS as readonly string[]).includes(k));
  // Operator-grained billing keys: hidden at KIOSK.
  if (scope === "KIOSK") keys = keys.filter(k => !(OPERATOR_LEVEL_KEYS as readonly string[]).includes(k));
  // Odoo profile: see scopeAllowsOdoo — the ONE place this rule lives.
  if (!scopeAllowsOdoo(scope))
    keys = keys.filter(k => !(ODOO_PROFILE_KEYS as readonly string[]).includes(k));
  // Reseller inbound: fee-schedule + billingMode only — drop partner identity.
  if (scope === "RESELLER_INBOUND")
    keys = keys.filter(k => k !== "partnerInfo");
  return keys;
}
